import { currencyShort } from '../components/ui'
import { CurrencyControl } from '../components/CurrencyControl'
import { useReportCurrency } from '../components/rememberedCurrency'
import { MIXED_UNITS, mixedUnitsHint } from '../components/units'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { PARTIAL_NOTE, PARTIAL_TITLE, WITHHELD, WITHHELD_TITLE, partialOf, type Answer } from '../api/answer'
import { Withheld, markPartial } from '../components/Withheld'
import type { Api } from '../api'
import { ApiError, LOCAL, USD, inCurrency, isConverted, isUsd, toParam } from '../api/types'
import type {
  ApiFailure, Currency, CurrencyRate, StoredReaderView, MpBreakdownRow, MpFilterKind, MpFilterRow, MpItemOrderRow, MpItemStatusRow,
  MpItemSummary, MpOrderStatus, MpOverview, MpPurchaseOrderRow, MpSalesMonth, MpSalesPerformanceRow,
  MpShipmentRow, MpShipmentView, MpStockGroupBy, MpStockRow, MpStockSort,
} from '../api/types'
import { DataGrid, type Column } from '../components/DataGrid'
import type { SortState } from '../components/gridSort'
import { ExpandableCard } from '../components/ExpandableCard'
import { DevModeProvider } from '../components/MeasurePanel'
import { Explain } from '../components/DevModeContext'
import { ExportButtons } from '../components/ExportButtons'
import { FailureState } from '../components/FailureState'
import { currencyMark } from '../components/CurrencyHover'
import { applyLayout, restore, snapshot } from '../components/gridLayout'
import { gridsOnTab } from '../api/viewScope'
import {
  NOTHING_DROPPED,
  SavedViewsMenu,
  VIEW_SCHEMA,
  ViewAdjustedNotice,
  parseView,
  useSavedViews,
  useViewDirty,
  type DropReport,
  type ViewPayload,
} from '../components/SavedViews'
import type { CellKind, ExportDoc } from '../export/model'
import {
  Button, Card, CardHeader, CollapsibleStats, EmptyState, Loading, PageTabs, ReportHeader,
  SearchInput, SearchSelect, Segmented, StatTile, compact, money,
} from '../components/ui'

/**
 * MATERIAL PLANNING — the Power BI report of the same name, rebuilt, and on
 * the second pass rebuilt WHOLE. Owner's directive, 8 Sep 2026: every field
 * and every measure of the report must be present; improve freely, omit
 * nothing. Then the business manager reviewed the published report and sent
 * back a page list with a column order for each. Ten screens:
 *
 *   Overview            the four cards, card for card, the donut and the bar
 *   Company Overview    HIS column order: PO YTD, PO PY, Diff, Sold YTD,
 *                       Open SO YTD, Sold PY, Diff, On Hand, On Order
 *   Inventory Position  the bookmark navigator, his matrix, his Difference
 *   Purchase Orders     Open / Under review / Closed, every column
 *   Shipment Tracker    HIS column order, and its own filters
 *   Arriving Soon       the tracker, ETA within thirty days
 *   Late Shipments      the tracker, ETA past with nothing received
 *   Item Status         UNCHANGED — he asked for it to stay as it was
 *   Sales Performance   quantity sold per product against last year
 *   Sales Trend         quantity sold by month, both years
 *
 * WHERE HE ASKED FOR FILTERS TO BECOME CARDS, they became cards and the
 * standing bar keeps the pickers. Removing a filter from a screen removes it
 * from a reader who was using it; the cards are what he wanted to SEE, and
 * they now sit above the grid answering the same selection.
 *
 * THIS IS A QUANTITY REPORT. Money is present, gated by its cost flag, and
 * always last.
 *
 * TWO DEFINITIONS OF "OPEN", both the Power BI report's own: the tiles and
 * the per-item figures count purchase orders whose file flag is 0, exactly as
 * the report's headline cards did; the Purchase Orders screen counts line
 * status 1 or 2, as its order pages did. They overlap on 971 of 33,476
 * production lines. Each is named where it is used.
 *
 * WHAT A BLANK CELL MEANS. Every quantity on this report is mapped to
 * Inv_Mod, which is the report's own Required_Permission — a reader who lacks
 * it cannot open the report at all — so a null quantity can only mean the
 * source never recorded it. 55% of the planning view is such rows: products
 * with no inventory record anywhere. They are hidden behind "Every item" and
 * drawn blank when shown, as the Power BI report drew them. Only the MONEY
 * columns can actually be withheld; those read as an em dash.
 */

type TabId =
  | 'overview' | 'company' | 'stock' | 'purchase'
  | 'shipments' | 'arriving' | 'late' | 'items' | 'performance' | 'trend'

/**
 * Ten screens have to share one row at 1280px, so the labels are shortened and
 * each carries the page's full name on hover. The strip does not wrap.
 */
const TABS: { id: TabId; label: string; title: string }[] = [
  { id: 'overview', label: 'Overview', title: 'The headline quantities, the cost donut and the sales bar chart' },
  { id: 'company', label: 'Company Overview', title: 'Purchases and sales per item, this year against last' },
  { id: 'stock', label: 'Inventory Position', title: 'Stock by company, unit, team or product, with the shortfall against open sales orders' },
  { id: 'purchase', label: 'Purchase Orders', title: 'Every purchase-order line, by status' },
  { id: 'shipments', label: 'Shipments', title: 'Shipment Tracker: confirmed open lines still to arrive' },
  { id: 'arriving', label: 'Arriving Soon', title: 'Lines with an ETA inside the next thirty days' },
  { id: 'late', label: 'Late', title: 'Late Shipments: an ETA already past with nothing received' },
  { id: 'items', label: 'Item Status', title: 'One item, or the whole selection, a year against the year before' },
  { id: 'performance', label: 'Sales', title: 'Sales Performance: quantity sold per product against last year' },
  { id: 'trend', label: 'Trend', title: 'Sales Trend: quantity sold by month, both years' },
]

/** Which shipment screen a tab is, and what the empty state should say. */
const SHIPMENT_VIEW: Partial<Record<TabId, MpShipmentView>> = {
  shipments: 'tracker', arriving: 'arriving-soon', late: 'late',
}

/** The Inventory Position bookmark navigator, in the manager's own order. */
const GROUP_LABEL: Record<MpStockGroupBy, string> = {
  company: 'Company', bu: 'Business unit', bt: 'Business team', product: 'Product', item: 'Item + warehouse',
}

const PAGE_SIZE = 100
const DATE_FMT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }

/**
 * A tile has no column beside it to look across at, so it spells the dimension
 * out: "kg", or "mixed units" when the selection holds several. Joined onto
 * whatever else the tile's second line already says -- and a mixture explains
 * itself on hover with the same sentence as the figures in the grids.
 *
 * dimWord is the PLAIN words, for subtitles and extracts, which cannot hover.
 */
const dimWord = (d: Dim | null) => (d ? d.text : null)
const withDim = (existing: string | undefined, d: Dim | null): ReactNode => {
  if (!d) return existing || undefined
  const word = d.mixed ? <span title={d.title}>{d.text}</span> : d.text
  return existing ? <>{existing} · {word}</> : word
}
/**
 * withDim for a KPI tile's currency line: the same words, with a single code
 * marked so the currency hover card can name it. "mixed currencies" and a
 * missing currency fall back to the plain words, because there is nothing
 * single to name.
 */
const withCur = (existing: string | undefined, d: Dim | null): ReactNode =>
  d?.currency
    ? existing ? <>{existing} · {currencyMark(d.currency)}</> : currencyMark(d.currency)
    : withDim(existing, d)

/**
 * What the "This page" row is in. A totals row has no unit column to look
 * across at either, and on a page that holds kilograms beside tonnes the total
 * is a sum of unlike things — so the label says so instead of standing there
 * looking like an answer, and says why on hover.
 *
 * `units` is what each row's figures carry: a unit, or "mixed units".
 */
function totalsOf(units: (string | null | undefined)[]): { totalsLabel: string; totalsTitle?: string } {
  const set = new Set(units.filter((u): u is string => !!u))
  // The mixture matters as much as the size: at a rollup grain EVERY row reads
  // "mixed units", so the set has one member and the total would have claimed
  // to be a single unit while being the least single figure on screen.
  if (set.size <= 1 && !set.has(MIXED_UNITS)) return { totalsLabel: 'This page' }
  return {
    totalsLabel: `This page · ${MIXED_UNITS}`,
    totalsTitle: mixedUnitsHint([...set].filter((u) => u !== MIXED_UNITS)),
  }
}

/** A quantity the source never recorded. Quiet, as Power BI drew it; the reason is on hover. */
/** A money figure withheld, or never costed. The only kind of cell that can actually be masked here. */
/* UNDER THE NEW MODEL a blank figure is withheld, and only that (058; M8): a figure
   the reader may see over no rows is 0. Under today's, a blank quantity was never
   recorded and blank money is masked or uncosted. The cells ask which model
   answered, so every helper below draws both. */
const NewModel = createContext(false)
/** The screens that show sales figures, where "Sales: your team" belongs. */
const SALES_TABS = new Set<string>(['overview', 'company', 'items', 'performance', 'trend'])
/** A figure the column rules govern but the source may simply lack: withheld only where no piece of access shows it. */
const governedBlank = (ctx: { v2: boolean; shown: ReadonlySet<string> | null }, name: string) =>
  ctx.v2 && !(ctx.shown?.has(name) ?? false) ? ABSENT : NOT_RECORDED
function Blank({ money: isMoney }: { money: boolean }) {
  const v2 = useContext(NewModel)
  if (v2) return <Withheld />
  return isMoney
    ? <span className="text-(--color-ink-4)" title="Not shown to you, or no cost recorded for this line">—</span>
    : <span className="text-(--color-ink-4)" title="Not recorded in the source">–</span>
}
const ABSENT = <Blank money={false} />
const MASKED = <Blank money />
/** A field the column rules do not govern, never recorded: the same under both models. */
const NOT_RECORDED = <span className="text-(--color-ink-4)" title="Not recorded in the source">–</span>
/** A figure that leaves withheld values out, by the name the row's Partial uses: its value and the mark. */
const marked = (row: object, name: string, node: ReactNode) =>
  markPartial(partialOf(row as { partial?: string | null }).has(name), node)
/** Under the new model, a client-summed column's figure on one row: withheld, or partly so (M8). */
const mpWithheld = (name: string) => (r: object): 'withheld' | 'partial' | null => {
  const v = (r as Record<string, unknown>)[name]
  return v === null ? 'withheld' : partialOf(r as { partial?: string | null }).has(name) ? 'partial' : null
}

/**
 * WHAT A FIGURE IS IN, and how the screen says it.
 *
 * A quantity without its unit is not a quantity: this report holds kilograms,
 * tonnes, litres, pieces, drums and fourteen other units, and its money spans
 * twenty-two currencies. Every figure carries its own, written beside it.
 *
 * The awkward case is a rollup. 37 of 43 companies and 116 of 226 business
 * units hold more than one unit of measure, so a company's On Hand really is
 * kilograms added to pieces. The procedures return a COUNT alongside the
 * value for exactly this: above one there is no true answer, and the screen
 * says "mixed units" rather than printing one member's unit over the sum. Three
 * states, all different: a unit, a mixture, and nothing recorded at all.
 *
 * "MIXED UNITS", NOT "MIXED" (4 Oct 2026). The figures said "157.6K mixed" --
 * mixed what? -- with a hover that said "2 different ones". The sales manager
 * asked for exactly this on the Sales Explorer ("Qty YTD (Mixed Units)") and
 * the owner pointed here the same day. The words and the sentence on hover are
 * shared with Sales: components/units.ts.
 */
interface Dim {
  text: string
  title?: string
  /* Set only when the dim IS one currency code, so the suffix can be marked for
     the currency hover card. A unit is not a currency, and a mixture names none. */
  currency?: string
  /** Several units, or several currencies, added together -- not one thing. */
  mixed?: boolean
}
/** A money figure that adds several currencies: Local, across companies. */
const MIXED_CURRENCIES = 'mixed currencies'
const dimOf = (value: string | null | undefined, count?: number | null, kind: 'unit' | 'currency' = 'unit'): Dim | null => {
  if (count !== null && count !== undefined && count > 1) {
    return kind === 'unit'
      ? { text: MIXED_UNITS, title: mixedUnitsHint([], count), mixed: true }
      : {
          text: MIXED_CURRENCIES,
          title: `Adds ${count} currencies together as booked, so this is not one amount. Choose USD or Custom to see it in one currency.`,
          mixed: true,
        }
  }
  return value ? { text: value } : null
}
/** dimOf for a currency code. Same rule in its own words, plus the marker. */
const curOf = (value: string | null | undefined, count?: number | null): Dim | null => {
  const d = dimOf(value, count, 'currency')
  return d && value && !d.mixed ? { ...d, currency: value } : d
}
/** A literal suffix for a figure whose unit is the column, not the row — pallets, days, orders. */
const dim = (text: string): Dim => ({ text })

/**
 * The suffix sits in its own inline-block so a column of figures still lines
 * up on the number: the digits stay tabular and right-aligned, and the unit
 * starts at a fixed offset after them rather than pushing them around.
 */
function Suffix({ d }: { d: Dim | null | undefined }) {
  if (!d) return null
  return (
    <span
      className="ml-1 inline-block min-w-[2.5ch] text-left text-(length:--text-small) font-normal text-(--color-ink-3)"
      title={d.title}
      data-currency={d.currency}
    >
      {d.text}
    </span>
  )
}

const num = (v: number | null | undefined, d?: Dim | null) =>
  v === null || v === undefined ? ABSENT : <><span className="tabular-nums">{compact(v)}</span><Suffix d={d} /></>
const cash = (v: number | null | undefined, d?: Dim | null) =>
  v === null || v === undefined ? MASKED : <><span className="tabular-nums">{money(v)}</span><Suffix d={d} /></>
const signed = (v: number | null | undefined, d?: Dim | null) =>
  v === null || v === undefined ? ABSENT
    : <><span className={`tabular-nums ${v < 0 ? 'font-semibold text-(--color-overdue)' : ''}`}>{compact(v)}</span><Suffix d={d} /></>
/**
 * The manager's Difference, which reads the OTHER way round: open sales orders
 * less what is on hand and on order, so a POSITIVE number is demand that stock
 * cannot meet. Coloured on positive for exactly that reason — the opposite of
 * `signed` above, and the two sit in adjacent columns, so neither is left to
 * be inferred from the sign.
 */
const shortOf = (v: number | null | undefined, d?: Dim | null) =>
  v === null || v === undefined ? ABSENT
    : <><span className={`tabular-nums ${v > 0 ? 'font-semibold text-(--color-overdue)' : 'text-(--color-ink-3)'}`}>{compact(v)}</span><Suffix d={d} /></>
const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? ABSENT
    : <span className={v < 0 ? 'text-(--color-overdue)' : undefined}>{(v * 100).toFixed(1)}%</span>
const date = (v: string | null | undefined) =>
  !v ? <span className="text-(--color-ink-4)">—</span> : new Date(v).toLocaleDateString('en-GB', DATE_FMT)
const dateS = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-GB', DATE_FMT) : null)

/**
 * THE BOTTOM ROW OF THE TWO ORDER GRIDS (7 Oct 2026): EVERY matching line, as
 * 027 totals it, never the page on screen.
 *
 * It used to add up the hundred rows showing, kilograms to pieces to litres,
 * and a reader who filtered to one business unit saw a bigger "total" than the
 * unfiltered screen -- two different pages, neither a total of anything. The
 * owner's decision: total every line that matches, and print a figure only when
 * those lines share one unit (one currency, for money). Otherwise the cell says
 * what it is instead of standing there looking like an answer.
 */
function allLineTotals(rows: (MpPurchaseOrderRow | MpShipmentRow)[], v2 = false): {
  totalsLabel: string; totalsTitle: string; totals: Record<string, ReactNode>
} {
  const first = rows[0]
  const n = first?.totalRows ?? rows.length
  const notAdded = (what: string, why: string) => (
    <span className="font-normal text-(--color-ink-3)" title={why}>{what}</span>
  )
  const qty = first?.totalQty ?? null
  const amount = first?.totalAmount ?? null
  return {
    totalsLabel: `All ${n.toLocaleString()} line${n === 1 ? '' : 's'}`,
    totalsTitle: 'Every line that matches the filters, on every page, not only the rows on screen.',
    totals: {
      // Null is the mask (or an API older than the totals): nothing to print.
      // Under the new model a blank total is withheld, and one that leaves lines out is marked (M8).
      qty: qty === null ? (v2 ? <Withheld /> : '')
        : first?.totalQtyUnit ? markPartial(partialOf(first).has('totalQty'), num(qty, dimOf(first.totalQtyUnit)))
        : notAdded(MIXED_UNITS, 'These lines are in more than one unit, or some have none, so adding them up would mean nothing.'),
      amount: amount === null ? (v2 ? <Withheld /> : '')
        : first?.totalAmountCurrency ? markPartial(partialOf(first).has('totalAmount'), cash(amount, curOf(first.totalAmountCurrency)))
        : notAdded(MIXED_CURRENCIES, 'These lines are in more than one currency, or some have no figure. Choose USD or Custom to total them in one currency.'),
    },
  }
}

/** How the new model sees one figure (M8); absent under today's model. */
interface Seen { v2: boolean; partial: boolean }
const numS = (v: number | null | undefined, s?: Seen) =>
  v === null || v === undefined ? (s?.v2 ? WITHHELD : '–') : `${compact(v)}${s?.partial ? '*' : ''}`
const cashS = (v: number | null | undefined, s?: Seen) =>
  v === null || v === undefined ? (s?.v2 ? WITHHELD : '—') : `${money(v)}${s?.partial ? '*' : ''}`
const fullQty = (v: number | null | undefined, s?: Seen) =>
  v === null || v === undefined ? (s?.v2 ? WITHHELD_TITLE : 'Not recorded in the source')
    : s?.partial ? `${v.toLocaleString()}. ${PARTIAL_TITLE}.` : v.toLocaleString()
const fullCash = (v: number | null | undefined, s?: Seen) =>
  v === null || v === undefined ? (s?.v2 ? WITHHELD_TITLE : 'Not shown to you, or no cost recorded')
    : s?.partial ? `${money(v)}. ${PARTIAL_TITLE}.` : money(v)

/** The approval codes, decoded from the model's one calculated column. */
const APPROVAL: Record<number, string> = {
  0: 'Draft', 10: 'In Review', 20: 'Rejected', 30: 'Approved', 35: 'Approved (transitional)', 40: 'Confirmed', 50: 'Finalized',
}
const approval = (c: number | null) => (c === null ? NOT_RECORDED : <>{APPROVAL[c] ?? `Code ${c}`}</>)
const approvalS = (c: number | null) => (c === null ? null : APPROVAL[c] ?? `Code ${c}`)
const LINE_STATUS: Record<number, string> = { 1: 'Open', 2: 'Received', 3: 'Invoiced', 4: 'Cancelled' }

const STATUS_LABEL: Record<MpOrderStatus, string> = {
  open: 'Open', review: 'Under review', closed: 'Closed', cancelled: 'Cancelled', all: 'All',
}
const SORT_LABEL: Record<MpStockSort, string> = {
  onhand: 'On hand', pallets: 'Pallets', movement: 'Oldest movement', item: 'Item number',
}

/** The standing filters. Every screen reads the first five; the rest belong to the screens named. */
interface Filters {
  company: string; bu: string; itemGroup: string; supplier: string; year: string
  warehouse: string
  modeOfDelivery: string; supplierCategory: string
  shipmentStatus: string; orderProgress: string; orderStatus: string
}
const NO_FILTERS: Filters = {
  company: '', bu: '', itemGroup: '', supplier: '', year: '', warehouse: '',
  modeOfDelivery: '', supplierCategory: '',
  shipmentStatus: '', orderProgress: '', orderStatus: '',
}
const FILTER_LABEL: Record<keyof Filters, string> = {
  company: 'Company', bu: 'Business unit', itemGroup: 'Item group', supplier: 'Supplier', year: 'Year',
  warehouse: 'Warehouse', modeOfDelivery: 'Mode of delivery',
  supplierCategory: 'Supplier category', shipmentStatus: 'Shipment status',
  orderProgress: 'Voyage status', orderStatus: 'Order status',
}

/* WHICH OPTION LIST EACH FILTER IS CHOSEN FROM. The picker rows above carry
   this pairing inline; a saved view needs it as data, to ask whether a value it
   stored months ago is still one this reader can be offered. */
const FILTER_KIND: Record<keyof Filters, MpFilterKind> = {
  company: 'company', bu: 'businessUnit', itemGroup: 'itemGroup', supplier: 'supplier', year: 'year',
  warehouse: 'warehouse', modeOfDelivery: 'modeOfDelivery',
  supplierCategory: 'supplierCategory', shipmentStatus: 'shipmentStatus',
  orderProgress: 'orderProgress', orderStatus: 'orderStatus',
}

/** The order-status values the Purchase Orders picker offers. */
const ORDER_STATUS: MpOrderStatus[] = ['open', 'review', 'closed', 'cancelled', 'all']

/** What every extract needs to describe itself: the file's reader was not at the screen. */
interface Ctx {
  currency: Currency; viewAs: string | null; search: string; page: number; filters: Filters
  /* The sort travels with the extract context because it IS context: a sheet
     of a hundred rows means something different when those hundred are the
     largest by line amount rather than the newest. Every grid hands these two
     straight to DataGrid, which is what puts it in server-sorted mode. */
  sort: SortState
  onSortChange: (next: SortState) => void
  /** The new permission model answered (M8). */
  v2: boolean
  /** Its sales figures are the reader's team's, through the hierarchy (D18). */
  teamSales: boolean
  /** The figures some piece of the reader's access shows, from the screen's reads; null under today's model. */
  shown: ReadonlySet<string> | null
}
const or = (v: string | undefined) => v || undefined
const yearOf = (f: Filters) => (f.year ? Number(f.year) : undefined)

const context = (ctx: Ctx, extra: string[] = []): string[] => [
  isUsd(ctx.currency)
    ? 'Currency: US dollars, converted per row off each line’s own currency'
    : 'Currency: local, as booked — figures from different companies are not comparable',
  ...(Object.keys(ctx.filters) as (keyof Filters)[])
    .filter((k) => ctx.filters[k])
    .map((k) => `${FILTER_LABEL[k]}: ${ctx.filters[k]}`),
  ...extra,
  ...(ctx.search ? [`Search: contains "${ctx.search}"`] : []),
]

const pageNote = (ctx: Ctx, rows: number, total: number | undefined) =>
  total && total > rows
    ? `Rows ${(ctx.page - 1) * PAGE_SIZE + 1}–${(ctx.page - 1) * PAGE_SIZE + rows} of ${total.toLocaleString()}. This extract is the page on screen, not the whole result.`
    : undefined

/** An extract column: the header the file shows, its kind, and the raw cell. */
interface XCol<T> { header: string; kind?: CellKind; cell: (r: T) => string | number | null }

function gridDoc<T>(
  ctx: Ctx, gridId: string, page: string, title: string, subtitle: string, cols: XCol<T>[], rows: T[],
  total: number | undefined, extra: string[] = [], note?: string,
): ExportDoc {
  /* THE EXTRACT IS THE SCREEN, still. Now that a reader can hide and reorder
     columns, that promise only holds if the file follows the grid — otherwise
     somebody who put six columns away gets them back in Excel and has to do the
     work twice. Matched by header, because this list is a different array from
     the on-screen one. Columns the grid never had (the raw Unit and Currency
     fields, which exist so a spreadsheet can sum them) are untouched. */
  const shown = applyLayout(gridId, cols)
  // Under the new model the file says what the screen says (M8): a blank figure is
  // withheld, and a partial one is noted, as a file carries no mark per cell.
  const FIGURE = new Set<CellKind | undefined>(['money', 'qty', 'num2'])
  const cell = (c: XCol<T>, r: T) => {
    const v = c.cell(r)
    return ctx.v2 && v === null && FIGURE.has(c.kind) ? WITHHELD : v
  }
  const partly = ctx.v2 && rows.some((r) => partialOf(r as { partial?: string | null }).size > 0)
  const pageLine = note ?? pageNote(ctx, rows.length, total)
  return {
    report: 'Material Planning', page, title, subtitle,
    context: context(ctx, extra), privileged: ctx.viewAs, currency: ctx.currency,
    tables: [{
      note: partly ? [pageLine, PARTIAL_NOTE].filter(Boolean).join(' ') : pageLine,
      columns: shown.map((c) => ({ header: c.header, kind: c.kind })),
      rows: rows.map((r) => ({ cells: shown.map((c) => cell(c, r)) })),
    }],
  }
}

/** One picker of the standing bar: an eyebrow, a SearchSelect, an "All" that clears it. */
function Pick({ label, value, onChange, options, measureKey }: {
  label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]
  /** Developer mode. Omit it and the label stays inert, which is right for a
      control nothing has been written about. */
  measureKey?: string
}) {
  return (
    <div className="flex min-w-[150px] flex-col gap-1 max-w-[240px]">
      <Explain surface="filters" measureKey={measureKey ?? ''} className="eyebrow">
        {label}
      </Explain>
      <SearchSelect label={label} value={value} onChange={onChange} options={[{ value: '', label: 'All' }, ...options]} />
    </div>
  )
}

export function MaterialPlanningPage({ api, viewAs, roles = [] }: {
  api: Api
  viewAs: string | null
  /** For developer mode. Affordance only -- the API refuses the panel to anyone
      who is not a System Admin whatever this says. */
  roles?: string[]
}) {
  const [tab, setTab] = useState<TabId>('overview')
  /* USD by default since 23 Sep 2026 — see SalesPage for the reasoning. It
     moves the MONEY columns only; quantities are counted in each line's own
     unit and no toggle can make kilograms and litres commensurable, which is
     what the unit caption on every card and grid is for. */
  /* Remembered since 4 Oct 2026, across every report: see
     components/rememberedCurrency.ts and the note in SalesPage. */
  const [currency, chooseCurrency, setCurrency] = useReportCurrency(api, viewAs)
  const [filters, setFilters] = useState<Filters>(NO_FILTERS)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<MpOrderStatus>('open')
  const [includeEmpty, setIncludeEmpty] = useState(false)
  const [sortBy, setSortBy] = useState<MpStockSort>('onhand')
  const [groupBy, setGroupBy] = useState<MpStockGroupBy>('item')
  /* THE COLUMN THE READER CLICKED, and it is sent to the SERVER.
     ------------------------------------------------------------------------
     Every grid on this report is paged at 100 rows, so sorting in the browser
     would reorder the page and look exactly like a sorted report. DataGrid
     refuses to sort a partial grid for that reason; handing it `sort` and
     `onSortChange` is how a paged grid gets a real one.

     Held here rather than per grid because the six grids are six tabs of one
     screen and only one is ever mounted, and because the fetch below is the
     thing that has to carry it. Cleared on every tab change: the key names a
     column of the grid that was showing, and Purchase Orders has no idea what
     'LastMovement' means. */
  const [gridSort, setGridSort] = useState<SortState>({ key: null, desc: true })
  const [selectedItem, setSelectedItem] = useState('')
  const [page, setPage] = useState(1)
  /** Which item's open orders the On Order panel is showing, if any. */
  const [ordersFor, setOrdersFor] = useState<{ item: string; company: string | null } | null>(null)

  /* NULL until the scoped picker lists land. A saved view decides what to DROP
     by asking what is offered, so applying one before they arrive would retract
     filters in front of the reader; an empty array means the read finished. */
  const [options, setOptions] = useState<MpFilterRow[] | null>(null)
  const [overview, setOverview] = useState<MpOverview | null>(null)
  /** The new permission model answered the screen's reads (M8). */
  const [v2, setV2] = useState(false)
  /** Their sales figures are the reader's team's, through the hierarchy (D18): "Sales: your team". */
  const [teamSales, setTeamSales] = useState(false)
  /** The figures some piece of the reader's access shows, from the screen's reads (M8). */
  const [shown, setShown] = useState<ReadonlySet<string> | null>(null)
  const [costByUnit, setCostByUnit] = useState<MpBreakdownRow[] | null>(null)
  const [qtyByCompany, setQtyByCompany] = useState<MpBreakdownRow[] | null>(null)
  const [stock, setStock] = useState<MpStockRow[] | null>(null)
  const [orders, setOrders] = useState<MpPurchaseOrderRow[] | null>(null)
  const [shipments, setShipments] = useState<MpShipmentRow[] | null>(null)
  const [items, setItems] = useState<MpItemStatusRow[] | null>(null)
  const [summary, setSummary] = useState<MpItemSummary | null>(null)
  const [performance, setPerformance] = useState<MpSalesPerformanceRow[] | null>(null)
  const [months, setMonths] = useState<MpSalesMonth[] | null>(null)
  const [itemOrders, setItemOrders] = useState<MpItemOrderRow[] | null>(null)
  /* The On Order panel's OWN failure. A read behind one secondary panel used to
     go to the page-level `fail`, so a timeout on it wiped the whole Inventory
     Position screen — grid, cards, tabs and filters — for a panel the reader
     can simply close. Shown inside the panel instead. */
  const [itemOrdersFailure, setItemOrdersFailure] = useState<ApiFailure | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  /* True while the tab's data is being (re)fetched. Distinct from the `!rows`
     guards, which only ever catch the FIRST load — see <Updating> in ui.tsx. */
  const [busy, setBusy] = useState(false)
  /* Bumped by "Try again". A dependency of every read below, so a retry
     re-runs the screen's fetch AND re-reads option lists or a side panel that
     failed alongside it; nothing else changes, so the reader keeps their tab,
     filters and page. */
  const [retry, setRetry] = useState(0)

  const fail = useCallback((e: unknown) => {
    setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not load Material Planning.' })
  }, [])

  const set = (k: keyof Filters) => (v: string) => setFilters((f) => ({ ...f, [k]: v }))
  const opts = useCallback((kind: MpFilterKind) => {
    const list = (options ?? []).filter((o) => o.kind === kind && o.code).map((o) => ({ value: o.code!, label: o.label ?? o.code! }))
    return kind === 'year' ? list.sort((a, b) => b.value.localeCompare(a.value)) : list
  }, [options])

  // The option lists once per identity; they are scoped to the caller's own rows.
  useEffect(() => {
    let cancelled = false
    api.materialPlanningFilters()
      .then((r) => { if (!cancelled) setOptions(r) })
      /* A REFUSAL STILL REACHES THE READER; AN ERROR ONLY EMPTIES THE PICKERS.
         Every Material Planning endpoint shares one gate, so a refusal here is
         the report's refusal and belongs on the page. A 500 is not: it used to
         replace a report that had loaded fine with "Something went wrong",
         for the sake of the picker lists. Sales degrades its pickers the same
         way (SalesFilterBar). Emptied rather than kept, because after a Login
         As switch the lists in state belong to the previous identity. */
      .catch((e: unknown) => {
        if (cancelled) return
        if (e instanceof ApiError && e.failure.kind !== 'error') fail(e)
        else setOptions([])
      })
    return () => { cancelled = true }
  }, [api, viewAs, fail, retry])

  // Paging resets whenever the question changes: narrowing a search while on
  // page 4 would otherwise show an empty grid over a non-empty result.
  useEffect(() => { setPage(1) }, [tab, search, status, currency, filters, includeEmpty, sortBy, groupBy, gridSort])

  /* A SORT DOES NOT SURVIVE A TAB CHANGE. `gridSort.key` is a column name of
     the grid that was on screen, and the six grids share almost none; left in
     place it would reach a procedure that does not know the name, be dropped
     silently, and leave the new grid drawing no arrow over rows it had not
     reordered. Cleared here, where the page reset already happens. */
  useEffect(() => { setGridSort({ key: null, desc: true }) }, [tab])

  // The On Order panel belongs to one row of one grid. Any change to the
  // question underneath it invalidates it, and a panel left open over a
  // different result would be quietly lying about which item it describes.
  useEffect(() => { setOrdersFor(null) }, [tab, search, currency, filters, groupBy, page])

  useEffect(() => {
    if (!ordersFor) { setItemOrders(null); setItemOrdersFailure(null); return }
    let cancelled = false
    setItemOrders(null)
    setItemOrdersFailure(null)
    api.materialPlanningItemOrders({
      item: ordersFor.item, company: ordersFor.company ?? undefined,
      currency,
    })
      .then((r) => { if (!cancelled) setItemOrders(r) })
      // Never mapped to `[]`: that would read "No live orders", which is a
      // statement about the item, and a failed read has made none.
      .catch((e: unknown) => {
        if (!cancelled) {
          setItemOrdersFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not load the open orders for this item.' })
        }
      })
    return () => { cancelled = true }
  }, [api, ordersFor, currency, viewAs, retry])

  useEffect(() => {
    let cancelled = false
    setFailure(null)
    /* EVERY REFETCH IS OTHERWISE SILENT. The `!rows` guards below render
       <Loading> only when there is nothing in state, which is true exactly
       once — the first load. Change a filter and the previous rows are still
       there, so nothing renders, nothing moves, and the page looks frozen for
       as long as the read takes. On this report that is seconds. */
    setBusy(true)
    const base = {
      currency,
      company: or(filters.company), bu: or(filters.bu), itemGroup: or(filters.itemGroup), supplier: or(filters.supplier),
    }
    /* `sortBy: undefined` when nothing is sorted, so the procedure keeps the
       report's own order — the client never names a default, because the
       default belongs to the report. */
    const paged = {
      search: search || undefined, page, pageSize: PAGE_SIZE,
      sortBy: gridSort.key ?? undefined, sortDesc: gridSort.desc,
    }
    const po = {
      /* THE PRODUCT'S SUPPLIER, the standing filter, since 7 Oct 2026. The order
         screens used to take a separate "PO supplier" (the order's own vendor)
         and ignore this one entirely; the owner removed the first, and 027 now
         reads this the way every other tab does. */
      supplier: base.supplier, modeOfDelivery: or(filters.modeOfDelivery),
      supplierCategory: or(filters.supplierCategory), bu: base.bu, itemGroup: base.itemGroup,
      company: base.company, currency,
    }
    // What the answers say about themselves: the model, and whose sales (M8).
    const saw = (...answers: Answer<unknown>[]) => {
      if (cancelled) return
      setV2(answers.some((a) => a.vis !== null))
      setTeamSales(answers.some((a) => a.vis?.teamSales === true))
      setShown(answers.some((a) => a.vis !== null)
        ? new Set(answers.flatMap((a) => [...(a.vis?.shown ?? [])]))
        : null)
    }
    // A screen's "your team" is its own reads' word, never the last screen's.
    setTeamSales(false)
    const run = async () => {
      try {
        if (tab === 'overview') {
          const [o, c, q] = await Promise.all([
            api.materialPlanningOverview(base),
            api.materialPlanningBreakdown({ ...base, kind: 'cost-by-unit' }),
            api.materialPlanningBreakdown({ ...base, kind: 'sales-qty-by-company', year: yearOf(filters) }),
          ])
          if (!cancelled) { setOverview(o.data); setCostByUnit(c.data); setQtyByCompany(q.data); saw(o, c, q) }
        } else if (tab === 'stock') {
          // The cards answer the STANDING filters, the grid answers those plus
          // the warehouse and the search box. Said on screen, not left to be
          // discovered by a reader whose card and grid disagree.
          const [r, o] = await Promise.all([
            /* TWO CONTROLS, ONE PARAMETER. The "Sort by" dropdown predates
               column sorting and still offers its four choices; a heading
               click now writes the same @SortBy. The click wins while it is
               set, and choosing from the dropdown clears it — otherwise the
               dropdown would appear to do nothing. `...paged` already carries
               the column sort, so it is spread AFTER the dropdown's value. */
            api.materialPlanningStock({
              ...base, warehouse: or(filters.warehouse), includeEmpty, groupBy,
              ...paged, sortBy: gridSort.key ?? sortBy,
            }),
            api.materialPlanningOverview(base),
          ])
          if (!cancelled) { setStock(r.data); setOverview(o.data); saw(r, o) }
        } else if (tab === 'purchase') {
          const r = await api.materialPlanningPurchaseOrders({ ...po, ...paged, status })
          if (!cancelled) { setOrders(r.data); saw(r) }
        } else if (SHIPMENT_VIEW[tab]) {
          const r = await api.materialPlanningShipments({
            ...po, ...paged, view: SHIPMENT_VIEW[tab],
            shipmentStatus: or(filters.shipmentStatus), orderProgress: or(filters.orderProgress),
            orderStatus: or(filters.orderStatus),
          })
          if (!cancelled) { setShipments(r.data); saw(r) }
        } else if (tab === 'items' || tab === 'company') {
          // ONE request for both screens: they are the same rows in a different
          // column order, which is exactly what the manager asked for. Item
          // Status also needs its summary; Company Overview needs the cards.
          const grid = api.materialPlanningItemStatus({ ...base, ...paged, year: yearOf(filters), includeEmpty })
          if (tab === 'items') {
            const [g, s] = await Promise.all([
              grid,
              api.materialPlanningItemSummary({ ...base, item: selectedItem || undefined, year: yearOf(filters) }),
            ])
            if (!cancelled) { setItems(g.data); setSummary(s.data); saw(g, s) }
          } else {
            const [g, o] = await Promise.all([grid, api.materialPlanningOverview(base)])
            if (!cancelled) { setItems(g.data); setOverview(o.data); saw(g, o) }
          }
        } else if (tab === 'performance') {
          const r = await api.materialPlanningSalesPerformance({ ...base, ...paged, year: yearOf(filters) })
          if (!cancelled) { setPerformance(r.data); saw(r) }
        } else {
          // The Overview row alongside, for the one thing the trend cannot
          // know about itself: which unit and currency its figures are in.
          const [r, o] = await Promise.all([
            api.materialPlanningSalesVsLy({ ...base, year: yearOf(filters) }),
            api.materialPlanningOverview(base),
          ])
          if (!cancelled) { setMonths(r.data); setOverview(o.data); saw(r, o) }
        }
      } catch (e) {
        if (!cancelled) fail(e)
      } finally {
        /* Guarded on `cancelled`, because a superseded fetch must not clear the
           flag: change two filters quickly and the first request finishes after
           the second has started, so an unguarded clear would stop saying
           "Updating" while a newer read is still running. */
        if (!cancelled) setBusy(false)
      }
    }
    void run()
    return () => { cancelled = true }
  }, [api, tab, currency, filters, search, status, includeEmpty, sortBy, groupBy, selectedItem, page, gridSort, viewAs, fail, retry])

  const totalRows =
    tab === 'stock' ? stock?.[0]?.totalRows
      : tab === 'purchase' ? orders?.[0]?.totalRows
      : SHIPMENT_VIEW[tab] ? shipments?.[0]?.totalRows
      : tab === 'items' || tab === 'company' ? items?.[0]?.totalRows
      : tab === 'performance' ? performance?.[0]?.totalRows
      : undefined
  const pages = totalRows ? Math.ceil(totalRows / PAGE_SIZE) : 1
  const ctx: Ctx = { currency, viewAs, search, page, filters, sort: gridSort, onSortChange: setGridSort, v2, teamSales, shown }

  /* EVERY HOOK RUNS BEFORE THE EARLY RETURN, and this one did not.
     ------------------------------------------------------------------------
     `useRef` sat BELOW `if (failure) return`, so the moment a request failed
     this component rendered one hook fewer than it had the render before.
     React's answer to that is to throw, which means a reader who hit an API
     error got "This view ran into a problem" — the error boundary — instead
     of the FailureState panel three lines down that exists to tell them what
     actually went wrong.

     So the bug hid its own diagnosis: the one screen written to explain a
     failure could never be reached by one. Found 20 Sep 2026, when the API
     started returning 8144 against a database that had not had 027 deployed
     yet; it has been latent since the ref was added.

     THE EARLY RETURN ITSELF IS GONE, 22 Sep 2026. It replaced the header, the
     tabs and the filter bar with a FailureState that had no retry, so a reader
     whose screen timed out could not switch tab, clear the filter that caused
     it, or try again — only leave the report. The failure now renders in the
     body slot below, with "Try again". If an early return ever comes back,
     every hook must still run above it. */
  const shipmentExport = useRef<(() => ExportDoc | null) | null>(null)

  const isShipment = !!SHIPMENT_VIEW[tab]
  const isPo = tab === 'purchase' || isShipment
  const gridTab = tab !== 'overview' && tab !== 'trend'
  // The heading names the SCREEN, and the eyebrow names the report — the shape
  // Sales and Receivables use. See ReportHeader in ui.tsx.
  const active = TABS.find((t) => t.id === tab) ?? TABS[0]

  /* ---------------------------------------------------------------------
     SAVED VIEWS. Same adapter as Receivables' and Sales', reading this page's
     own state — see ReceivablesAgingPage for the full reasoning behind every
     rule it follows.

     Material Planning captures the most of the three, because it holds the
     most: twelve standing filters, the search box, four tab-local pickers and
     the sort. The sort is page state HERE and nowhere else, which is why this
     is the report where capturing it costs nothing.

     Declared BELOW the effect that clears the sort on a tab change, and that
     order is load-bearing: both fire on the same commit when a tab changes, in
     declaration order, so the view's sort is applied after the clear rather
     than being wiped by it.
     --------------------------------------------------------------------- */
  const { views, save, remove, rename, setDefault } = useSavedViews(api, 'material-planning', viewAs)
  const [activeView, setActiveView] = useState<number | null>(null)
  const [adjusted, setAdjusted] = useState<DropReport>(NOTHING_DROPPED)
  /** Which tab's default has already been applied, so it happens once. */
  const appliedDefault = useRef<Set<string>>(new Set())

  const [rates, setRates] = useState<CurrencyRate[] | null>(null)
  useEffect(() => {
    let cancelled = false
    api.currencyRates().then((r) => !cancelled && setRates(r)).catch(() => !cancelled && setRates([]))
    return () => { cancelled = true }
  }, [api])

  const tabViews = useMemo(() => views.filter((v) => v.pageSlug === tab), [views, tab])

  const capture = useCallback((): ViewPayload => {
    /* THE SORT GOES IN THE GRID'S OWN SLOT, as it does on the other two
       reports, although this page holds one sort for all six grids: only one is
       ever mounted, and a view is saved per tab. One payload shape across three
       reports is worth the small indirection. Widths and pinning stay with the
       silent layer, which keeps them per person per grid regardless. */
    const grids: Record<string, { order?: string[]; hidden?: string[]; sort?: SortState }> = {}
    for (const gridId of gridsOnTab('material-planning', tab)) {
      const snap = snapshot(gridId)
      if (!snap && !gridSort.key) continue
      grids[gridId] = {
        ...(snap ? { order: snap.order, hidden: snap.hidden } : {}),
        ...(gridSort.key ? { sort: gridSort } : {}),
      }
    }
    return {
      v: VIEW_SCHEMA,
      currency: toParam(currency) ?? 'local',
      filters,
      search,
      status,
      includeEmpty,
      sortBy,
      groupBy,
      grids,
    }
  }, [tab, currency, filters, search, status, includeEmpty, sortBy, groupBy, gridSort])

  const apply = useCallback(
    (payload: ViewPayload): DropReport => {
      const dropped: string[] = []
      const offered = (kind: MpFilterKind, code: string) =>
        !options || options.some((o) => o.kind === kind && o.code === code)

      /* FILTERS: a value the reader can no longer be offered is left out rather
         than applied — a picker naming a warehouse the book does not contain is
         the screen lying about what it is showing. */
      const stored = (payload.filters ?? {}) as Partial<Record<keyof Filters, unknown>>
      const next: Filters = { ...NO_FILTERS }
      for (const [key, kind] of Object.entries(FILTER_KIND) as [keyof Filters, MpFilterKind][]) {
        const v = stored[key]
        if (typeof v !== 'string' || v === '') continue
        if (!offered(kind, v)) {
          dropped.push(FILTER_LABEL[key].toLowerCase())
          continue
        }
        next[key] = v
      }
      /* "PO supplier" WAS REMOVED on 7 Oct 2026. A view saved with one names it
         as dropped rather than losing it silently. */
      if (typeof (stored as Record<string, unknown>).poSupplier === 'string' && (stored as Record<string, unknown>).poSupplier !== '') {
        dropped.push('PO supplier (removed from this report)')
      }
      setFilters(next)

      /* THE TAB'S OWN PICKERS. Each is checked against the values that exist
         rather than trusted: a payload is a string this page wrote months ago,
         and a `sortBy` the grid has since dropped would sort by nothing while
         the control claimed otherwise. */
      setSearch(typeof payload.search === 'string' ? payload.search.slice(0, 120) : '')
      setIncludeEmpty(payload.includeEmpty === true)
      if (ORDER_STATUS.includes(payload.status as MpOrderStatus)) setStatus(payload.status as MpOrderStatus)
      else setStatus('open')
      if (typeof payload.sortBy === 'string' && payload.sortBy in SORT_LABEL) setSortBy(payload.sortBy as MpStockSort)
      else setSortBy('onhand')
      if (typeof payload.groupBy === 'string' && payload.groupBy in GROUP_LABEL) setGroupBy(payload.groupBy as MpStockGroupBy)
      else setGroupBy('item')

      /* CURRENCY: the CODE is stored, never the rate. The client's rate is
         display-only, so a stored one would print an old number over figures
         converted at today's. */
      const code = typeof payload.currency === 'string' ? payload.currency : 'local'
      if (code === 'local') setCurrency(LOCAL)
      else if (code === 'usd') setCurrency(USD)
      else if (rates) {
        const live = rates.find((r) => r.code.toLowerCase() === code.toLowerCase())
        if (live) setCurrency(inCurrency(live.code, live.ratePerUsd))
        else dropped.push(`the ${code.toUpperCase()} currency`)
      }

      // GRIDS: filtered to the columns this reader actually has.
      const grids = (payload.grids ?? {}) as Record<string, { order?: string[]; hidden?: string[]; sort?: { key?: unknown; desc?: unknown } }>
      for (const [gridId, g] of Object.entries(grids)) {
        const current = snapshot(gridId)
        if (!current || !Array.isArray(g.order)) continue
        restore(gridId, {
          ...current,
          order: g.order.filter((k) => current.order.includes(k)),
          hidden: (g.hidden ?? []).filter((k) => current.order.includes(k)),
        })
      }

      /* The sort of whichever grid this tab draws, or its natural order where
         the view carries none. The key is a column name the SERVER has to know;
         one it does not is clamped there and recorded separately as a probe. */
      const s = grids[gridsOnTab('material-planning', tab)[0] ?? '']?.sort
      setGridSort(
        s && typeof s.key === 'string' && s.key ? { key: s.key, desc: s.desc !== false } : { key: null, desc: true },
      )

      return { dropped }
    },
    [options, rates, tab],
  )

  /* See ReceivablesAgingPage: the chip is a caption beside the title. */
  const viewDirty = useViewDirty(views, activeView, capture)

  const applyStored = useCallback(
    (v: StoredReaderView) => {
      const payload = parseView(v.view, v.schemaVersion)
      if (!payload) return
      setAdjusted(apply(payload))
      setActiveView(v.viewId)
    },
    [apply],
  )

  /* THE DEFAULT, ONCE PER TAB, AND ONLY AFTER THE PICKERS HAVE LANDED. */
  useEffect(() => {
    if (!options || !rates) return
    if (appliedDefault.current.has(tab)) return
    const def = views.find((v) => v.pageSlug === tab && v.isDefault)
    appliedDefault.current.add(tab)
    if (def) applyStored(def)
  }, [options, rates, views, tab, applyStored])

  // A different pair of eyes means different views and a different book.
  useEffect(() => {
    appliedDefault.current = new Set()
    setActiveView(null)
    setAdjusted(NOTHING_DROPPED)
  }, [viewAs])

  return (
    /* Developer mode, once, for all ten screens. The six grids get it from one
       `surface` prop each; every header under them becomes explainable with no
       other change at the call site. */
    <NewModel.Provider value={v2}>
    <DevModeProvider
      api={api}
      roles={roles}
      report="material-planning"
      request={{
        Screen: tab,
        Currency: currencyShort(currency),
        ...(filters.company ? { Company: filters.company } : {}),
        ...(filters.bu ? { 'Business unit': filters.bu } : {}),
        ...(filters.itemGroup ? { 'Item group': filters.itemGroup } : {}),
        ...(filters.supplier ? { Supplier: filters.supplier } : {}),
        ...(search ? { Search: search } : {}),
      }}
    >
    <div className="stack-tight flex h-full flex-col gap-4">
      {/* THE SAME HEADER AND THE SAME RAIL AS EVERY OTHER REPORT, 20 Sep 2026.
          This screen used to carry its own: the report's name as the heading
          rather than the screen's, no sentence saying whose data this is, and
          the ten screens offered as a Segmented pill group — the control that
          means "the same figures, expressed differently". Nothing about that
          was decided; there was simply no shared component to reach for when
          this report was written, so it invented chrome that happened to
          differ from the two reports beside it. A reader who holds all three
          had to learn this page twice.

          The print-only attribution that used to live here has not been
          dropped — it moved INTO ReportHeader, so all three reports now print
          it rather than this one alone. */}
      <ReportHeader
        report="Material Planning"
        screen={active.label}
        what="Stock, purchases and shipments."
        viewAs={viewAs}
        view={
          <SavedViewsMenu
            views={tabViews}
            activeId={activeView}
            acting={viewAs}
            dirty={viewDirty}
            onApply={applyStored}
            onRename={rename}
            onSave={async (name, makeDefault) => {
              setActiveView(await save(tab, name, capture(), makeDefault))
            }}
            onDelete={(id) => {
              void remove(id)
              if (id === activeView) setActiveView(null)
            }}
            onSetDefault={(id, on) => void setDefault(id, on)}
          />
        }
        right={
          <>
            {gridTab ? <SearchInput value={search} onChange={setSearch} placeholder="Item, product or PO…" label="Search" /> : null}
            <CurrencyControl api={api} label="Currency" value={currency} onChange={chooseCurrency} />
          </>
        }
      />

      <PageTabs
        tabs={TABS}
        value={tab}
        onChange={setTab}
        label="Material Planning pages"
        surface="filters"
        measureKey="view"
      />

      <ViewAdjustedNotice report={adjusted} onDismiss={() => setAdjusted(NOTHING_DROPPED)} />
      {/* "SALES: YOUR TEAM" (D18). Under the new model a sales figure comes through
          the reader's hierarchy: their own and their staff's sales of an item, not
          everybody's. Said once, above every screen whose sales sit below it. */}
      {teamSales && SALES_TABS.has(tab) ? (
        <p className="text-(length:--text-small) text-(--color-ink-3)">
          <span className="font-semibold text-(--color-ink-2)">Sales: your team.</span>{' '}
          The sales figures here are your own and your staff&rsquo;s, not all of each item&rsquo;s sales.
        </p>
      ) : null}

      {/* THE STANDING FILTER BAR. In the Power BI report these were 64 slicers
          across eleven pages: Business Unit on all eleven, Item Group on eight,
          Company on seven, Supplier and Year on five. Here they are once, and
          they follow the reader from screen to screen. */}
      <div className="flex flex-wrap items-end gap-3">
        <Pick measureKey="company" label="Company" value={filters.company} onChange={set('company')} options={opts('company')} />
        <Pick measureKey="bu" label="Business unit" value={filters.bu} onChange={set('bu')} options={opts('businessUnit')} />
        <Pick measureKey="itemGroup" label="Item group" value={filters.itemGroup} onChange={set('itemGroup')} options={opts('itemGroup')} />
        <Pick measureKey="supplier" label="Supplier" value={filters.supplier} onChange={set('supplier')} options={opts('supplier')} />
        {tab === 'overview' || tab === 'company' || tab === 'items' || tab === 'performance' || tab === 'trend' ? (
          <Pick measureKey="year" label="Year" value={filters.year} onChange={set('year')} options={opts('year')} />
        ) : null}
        {tab === 'stock' ? (
          <>
            <Pick measureKey="warehouse" label="Warehouse" value={filters.warehouse} onChange={set('warehouse')} options={opts('warehouse')} />
            {/* THE BOOKMARK NAVIGATOR from the manager's notes. In Power BI
                these were four bookmarks switching between four visuals; here
                the grain is a parameter and the grid keeps its shape, so a
                reader can move between them without losing their filters. */}
            <div className="flex flex-col gap-1">
              <Explain surface="filters" measureKey="groupBy" className="eyebrow">
                Group by
              </Explain>
              <Segmented
                label="Group by"
                options={(Object.keys(GROUP_LABEL) as MpStockGroupBy[]).map((g) => ({ value: g, label: GROUP_LABEL[g] }))}
                value={groupBy}
                onChange={setGroupBy}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Explain surface="filters" measureKey="sortBy" className="eyebrow">
                Sort by
              </Explain>
              <Segmented
                label="Sort by"
                options={(Object.keys(SORT_LABEL) as MpStockSort[]).map((s) => ({ value: s, label: SORT_LABEL[s] }))}
                value={sortBy}
                // Clears the column sort, which otherwise outranks this and
                // would make the dropdown look broken. See the stock fetch.
                onChange={(s) => { setSortBy(s); setGridSort({ key: null, desc: true }) }}
              />
            </div>
          </>
        ) : null}
        {isPo ? (
          <>
            <Pick measureKey="modeOfDelivery" label="Mode of delivery" value={filters.modeOfDelivery} onChange={set('modeOfDelivery')} options={opts('modeOfDelivery')} />
            <Pick measureKey="supplierCategory" label="Supplier category" value={filters.supplierCategory} onChange={set('supplierCategory')} options={opts('supplierCategory')} />
          </>
        ) : null}
        {isShipment ? (
          <>
            {/* Three different columns the report shows side by side, so each
                gets its own picker rather than one standing in for the rest. */}
            <Pick measureKey="shipmentStatus" label="Shipment status" value={filters.shipmentStatus} onChange={set('shipmentStatus')} options={opts('shipmentStatus')} />
            <Pick measureKey="orderStatus" label="Order status" value={filters.orderStatus} onChange={set('orderStatus')} options={opts('orderStatus')} />
            <Pick measureKey="orderProgress" label="Voyage status" value={filters.orderProgress} onChange={set('orderProgress')} options={opts('orderProgress')} />
          </>
        ) : null}
        {tab === 'stock' || tab === 'items' ? (
          <div className="flex flex-col gap-1">
            <span className="eyebrow">Rows</span>
            {/* 55% of the planning view has no inventory record at all. Hidden
                by default, as Power BI hid an all-blank row; never hidden from
                a reader who asks. */}
            <Segmented
              surface="filters"
              measureKey="rows"
              label="Rows"
              options={[
                { value: 'live', label: 'With stock or movement', title: 'Items with something on hand, on order, reserved, valued, ordered or sold' },
                { value: 'all', label: 'Every item', title: 'Including items with no inventory record at all' },
              ]}
              value={includeEmpty ? 'all' : 'live'}
              onChange={(v) => setIncludeEmpty(v === 'all')}
            />
          </div>
        ) : null}
        {Object.values(filters).some(Boolean) ? (
          <Button onClick={() => setFilters(NO_FILTERS)}>Clear filters</Button>
        ) : null}
        {/* THE HOISTED EXTRACT IS GONE, 16 Sep 2026, and the reason is worth
            keeping. It existed because the shipment card set rightHidesOnShort:
            on a short screen the header dropped both its title and its buttons,
            so EXTRACT was rendered here instead, at the empty end of the filter
            bar, costing no height.

            That card is now an ExpandableCard, which deliberately does NOT offer
            rightHidesOnShort — the right slot carries the full-screen toggle,
            and hiding it would take the control away exactly where a reader most
            wants it, on the shortest screen. So the header keeps its buttons at
            every height, and a second copy here would be two EXTRACT controls
            side by side on a short screen. */}
      </div>

      {!isConverted(currency) && tab === 'overview' ? (
        <p className="text-(length:--text-small) text-(--color-ink-3)">
          Local money adds different currencies together. Quantities are unaffected; switch to USD to compare values across companies.
        </p>
      ) : null}

      {/* THE SAME <Loading> THE OTHER REPORTS USE, driven by `busy` rather than
          by `!rows`.

          Every guard inside the tabs below is `!rows`, which is true exactly
          once — on the first load. Change a filter and the previous rows are
          still in state, so nothing rendered, nothing moved, and the stale
          numbers were silently replaced whenever the read finished. Reported
          from production on day one: choosing a company "takes time but without
          loading, so it is confusing". Material Planning is the worst place for
          it, because its scoped reads run seconds.

          Sales already did this correctly — `{loading && <Loading/>}`, a flag
          rather than a data check — so this is Material Planning catching up
          with the platform, not a new pattern. One gate here rather than eleven
          inside the tabs: they all render from the same fetch, so they are all
          stale at the same moment, and a single early return cannot drift out
          of step with itself.

          A FAILURE TAKES THE SAME SLOT, and only this slot — the way Sales and
          Receivables draw theirs inside the tab. The chrome above stays up, so
          a refusal still reads as a refusal, while an error can be retried or
          escaped by changing tab or filter: the fetch effect clears `failure`
          on every re-run. */}
      {failure ? <FailureState failure={failure} onRetry={() => setRetry((n) => n + 1)} />
        : busy ? <Loading label="Loading Material Planning…" /> : (
      <>
      {tab === 'overview' ? <OverviewTab data={overview} cost={costByUnit} sold={qtyByCompany} ctx={ctx} /> : null}
      {tab === 'company' ? <CompanyOverviewTab rows={items} cards={overview} ctx={ctx} includeEmpty={includeEmpty} /> : null}
      {tab === 'stock' ? (
        <StockTab
          rows={stock} cards={overview} ctx={ctx} sortBy={sortBy} groupBy={groupBy} includeEmpty={includeEmpty}
          refined={!!filters.warehouse || !!search}
          ordersFor={ordersFor} orders={itemOrders} ordersFailure={itemOrdersFailure} onOrders={setOrdersFor}
        />
      ) : null}
      {tab === 'purchase' ? <OrdersTab rows={orders} status={status} onStatus={setStatus} ctx={ctx} /> : null}
      {isShipment ? (
        <ShipmentsTab
          rows={shipments} ctx={ctx} view={SHIPMENT_VIEW[tab]!} exportRef={shipmentExport}
        />
      ) : null}
      {tab === 'items' ? (
        <ItemsTab rows={items} summary={summary} selected={selectedItem} onSelect={setSelectedItem} ctx={ctx} includeEmpty={includeEmpty} />
      ) : null}
      {tab === 'performance' ? <PerformanceTab rows={performance} ctx={ctx} /> : null}
      {tab === 'trend' ? <TrendTab rows={months} cards={overview} ctx={ctx} /> : null}
      </>
      )}

      {/* Not under a failure: `totalRows` is read from the last rows that DID
          load, and a pager counting them beneath an error panel would describe
          a result the screen is not showing. */}
      {!failure && totalRows !== undefined && totalRows > PAGE_SIZE ? (
        <div className="flex items-center justify-between text-(length:--text-small) text-(--color-ink-3)">
          <span>
            {((page - 1) * PAGE_SIZE + 1).toLocaleString()}–{Math.min(page * PAGE_SIZE, totalRows).toLocaleString()} of {totalRows.toLocaleString()}
          </span>
          <span className="flex items-center gap-2">
            <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Previous</Button>
            <span>Page {page} of {pages}</span>
            <Button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>Next</Button>
          </span>
        </div>
      ) : null}
    </div>
    </DevModeProvider>
    </NewModel.Provider>
  )
}

/* ---------------------------------------------------------------- overview */

function Bars({ rows, py, fmt }: { rows: MpBreakdownRow[]; py: boolean; fmt: (v: number | null) => ReactNode }) {
  const peak = Math.max(1, ...rows.flatMap((r) => [r.value ?? 0, r.valuePY ?? 0]))
  return (
    <div className="flex flex-col gap-2 p-4">
      {rows.map((r) => (
        <div key={r.code ?? r.label ?? ''} className="flex items-center gap-3">
          <span className="w-40 shrink-0 truncate text-(length:--text-small) text-(--color-ink-2)" title={r.label ?? undefined}>{r.label}</span>
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="h-3 rounded-sm bg-(--color-navy-600)" style={{ width: `${((r.value ?? 0) / peak) * 100}%` }} />
            {py ? <span className="h-3 rounded-sm bg-(--color-navy-200)" style={{ width: `${((r.valuePY ?? 0) / peak) * 100}%` }} /> : null}
          </span>
          <span className="w-24 shrink-0 text-right text-(length:--text-small) tabular-nums text-(--color-ink)">{marked(r, 'value', fmt(r.value))}</span>
          {py ? <span className="w-24 shrink-0 text-right text-(length:--text-small) tabular-nums text-(--color-ink-3)">{marked(r, 'valuePY', fmt(r.valuePY))}</span> : null}
        </div>
      ))}
      {py ? (
        <p className="sticky bottom-0 -mx-4 -mb-4 mt-1 bg-(--color-surface) px-4 pt-2 pb-4 text-(length:--text-small) text-(--color-ink-3)">
          <span className="mr-1 inline-block h-2 w-3 rounded-sm bg-(--color-navy-600)" /> this year
          <span className="ml-3 mr-1 inline-block h-2 w-3 rounded-sm bg-(--color-navy-200)" /> last year
        </p>
      ) : null}
    </div>
  )
}

function OverviewTab({ data, cost, sold, ctx }: {
  data: MpOverview | null; cost: MpBreakdownRow[] | null; sold: MpBreakdownRow[] | null; ctx: Ctx
}) {
  const st = (o: { partial?: string | null } | null | undefined, name: string): Seen => ({ v2: ctx.v2, partial: partialOf(o).has(name) })
  /** A count: withheld under the new model when blank, else as before (0 for a blank under today's). */
  const count = (v: number | null | undefined, name: string) =>
    v === null && ctx.v2 ? WITHHELD : `${(v ?? 0).toLocaleString()}${st(data, name).partial ? '*' : ''}`
  const buildDoc = (): ExportDoc | null => {
    if (!data) return null
    return {
      report: 'Material Planning', page: 'Overview', title: 'Material Planning — Overview',
      /* No freshness subtitle: removed 15 Sep 2026 with the on-screen watermark.
         Production carries invoices dated ahead of today, so "data runs through
         <max invoice date>" printed a future date into the extract as well. */
      context: context(ctx), privileged: ctx.viewAs, currency: ctx.currency,
      kpis: [
        { label: 'On hand', value: numS(data.onHand, st(data, 'onHand')) },
        { label: 'On order', value: numS(data.onOrder, st(data, 'onOrder')) },
        { label: 'Reserved', value: numS(data.reserved, st(data, 'reserved')) },
        { label: 'Open PO quantity', value: numS(data.openPoQty, st(data, 'openPoQty')), secondary: 'file flag open' },
        { label: 'Open POs', value: count(data.openPoCount, 'openPoCount') },
        { label: 'Sold this year', value: numS(data.ytdQtySold, st(data, 'ytdQtySold')) },
        { label: 'Below minimum', value: count(data.belowMinItems, 'belowMinItems'), secondary: `${numS(data.belowMinShortfall, st(data, 'belowMinShortfall'))} units short` },
        { label: 'Inventory cost', value: cashS(data.inventoryValue, st(data, 'inventoryValue')) },
      ],
      tables: [
        {
          title: 'Cost by business unit',
          columns: [{ header: 'Business unit' }, { header: 'Cost', kind: 'money' }],
          rows: (cost ?? []).map((r) => ({ cells: [r.label, r.value] })),
        },
        {
          title: 'Quantity sold by company, absolute',
          note: 'Absolute quantity, as the Power BI chart counted it: a credit note adds here. The tile above is signed.',
          columns: [{ header: 'Company' }, { header: 'This year', kind: 'num2' }, { header: 'Last year', kind: 'num2' }],
          rows: (sold ?? []).map((r) => ({ cells: [r.label, r.value, r.valuePY] })),
        },
      ],
    }
  }

  if (!data) return <Loading label="Loading the overview…" />
  /* ASKED, NOT INFERRED. This used to read "every row's value is null, therefore
     the reader is not allowed cost" — a guess that is right often enough to be
     believed and wrong exactly when it matters. A masked measure travels as null
     so it can never render as a confident zero; "no cost was recorded" travels
     as null too, and from the values alone the two are identical.

     Reported the day this went live: a System Admin holding full bypass opened a
     company with no stock and was told "Cost is not shown to you. This breakdown
     needs the inventory-cost permission." Both sentences were false — bypass
     short-circuits the column mask, so he could see every column; the company
     simply had none. The procedure knows which it is, because it computes the
     mask, so it now says so. */
  const noCost = data.canSeeCost === false
  const u = dimOf(data.unit, data.unitCount)
  const c = curOf(data.currencyCode, data.currencyCount)
  return (
    <>
      {/* The freshness line that used to sit opposite the export button was
          removed 15 Sep 2026 — see SalesPage.tsx for the full reasoning. */}
      <div className="flex items-center justify-end gap-3">
        <ExportButtons build={buildDoc} />
      </div>

      <CollapsibleStats storageKey="stats:material-planning">
        <div className="stat-strip">
          <StatTile surface="overview" measureKey="onHand" label="On hand" value={numS(data.onHand, st(data, 'onHand'))} fullValue={fullQty(data.onHand, st(data, 'onHand'))} secondary={withDim(undefined, u)} tone="accent" dense />
          <StatTile surface="overview" measureKey="onOrder" label="On order" value={numS(data.onOrder, st(data, 'onOrder'))} fullValue={fullQty(data.onOrder, st(data, 'onOrder'))} secondary={withDim(undefined, u)} dense />
          <StatTile surface="overview" measureKey="reserved" label="Reserved" value={numS(data.reserved, st(data, 'reserved'))} fullValue={fullQty(data.reserved, st(data, 'reserved'))} secondary={withDim(undefined, u)} dense />
          <StatTile surface="overview" measureKey="openPoQty" label="Open PO quantity" value={numS(data.openPoQty, st(data, 'openPoQty'))} fullValue={fullQty(data.openPoQty, st(data, 'openPoQty'))} secondary={withDim('orders whose file is open', u)} dense />
          <StatTile surface="overview" measureKey="openPoCount" label="Open POs" value={count(data.openPoCount, 'openPoCount')} dense />
          <StatTile surface="overview" measureKey="ytdQtySold" label="Sold this year" value={numS(data.ytdQtySold, st(data, 'ytdQtySold'))} fullValue={fullQty(data.ytdQtySold, st(data, 'ytdQtySold'))} secondary={withDim('signed — credits subtract', u)} dense />
          <StatTile
            surface="overview"
            measureKey="belowMinItems"
            label="Below minimum"
            value={count(data.belowMinItems, 'belowMinItems')}
            secondary={withDim(`${numS(data.belowMinShortfall, st(data, 'belowMinShortfall'))} short`, u)}
            tone={data.belowMinItems ? 'bad' : undefined}
            dense
          />
          <StatTile surface="overview" measureKey="inventoryValue" label="Inventory cost" value={cashS(data.inventoryValue, st(data, 'inventoryValue'))} fullValue={fullCash(data.inventoryValue, st(data, 'inventoryValue'))} secondary={withCur(undefined, c)} dense />
        </div>
      </CollapsibleStats>

      <div className="flex min-h-0 flex-1 flex-col gap-4 @3xl:flex-row">
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardHeader
            title="Cost by business unit"
            subtitle={<>{`What the stock is worth, by unit. The report's donut. Figures in `}{c?.currency ? currencyMark(c.currency) : (dimWord(c) ?? 'the source currency')}.</>}
          />
          <div className="scroll-slim min-h-0 flex-1 overflow-auto">
          {!cost ? <Loading label="Loading…" />
            : noCost ? <EmptyState title="Cost is not shown to you" detail={ctx.v2 ? 'Your access does not show you cost here.' : 'This breakdown needs the inventory-cost permission.'} />
            : cost.length === 0 ? <EmptyState title="Nothing to show" detail="No stock within this selection." />
            /* THE CASE THAT WAS BEING MISREPORTED: rows exist, the reader may
               see cost, and none is recorded. Previously this fell through to
               the chart and drew nothing, or — worse — was caught by the old
               "all null therefore masked" guess and blamed a permission. */
            : cost.every((r) => r.value === null)
              ? <EmptyState title="No cost recorded" detail={ctx.v2
                  ? 'These items are within your selection, but none shows a cost: your access withholds it here, or none is recorded in the source.'
                  : 'These items are within your selection, but none of them carries a cost in the source.'} />
            : <Bars rows={cost} py={false} fmt={cash} />}
          </div>
        </Card>
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardHeader title="Quantity sold by company" subtitle={`This year against last, absolute — a credit note adds. The report's bar chart. Quantities in ${dimWord(u) ?? 'the source unit'}.`} />
          <div className="scroll-slim min-h-0 flex-1 overflow-auto">
          {!sold ? <Loading label="Loading…" />
            : data.canSeeQtySold === false
              ? <EmptyState title="Quantity sold is not shown to you" detail="This chart needs the inventory module's sales quantity." />
            : sold.length === 0 ? <EmptyState title="No sales to show" detail="No invoices within this selection." />
            : sold.every((r) => r.value === null && r.valuePY === null)
              ? <EmptyState title="No quantity recorded" detail={ctx.v2
                  ? 'These companies are within your selection, but none shows a sales quantity: your access withholds it here, or none is recorded.'
                  : 'These companies are within your selection, but no sales quantity is recorded against them.'} />
            : <Bars rows={sold} py fmt={num} />}
          </div>
        </Card>
      </div>
    </>
  )
}

/* -------------------------------------------------- company overview */

/**
 * THE MANAGER'S COLUMN ORDER, written out as he wrote it: PO YTD, PO PY, Diff,
 * Sold YTD, Open SO YTD, Sold PY, Diff, On Hand, On Order. The two Diff columns
 * are different subtractions and sit far apart in his list, so each says which
 * one it is in its own header rather than relying on position.
 *
 * The four filters this page used to carry became the cards above the grid.
 * The pickers stay in the standing bar, because a filter removed is a filter
 * taken away from whoever was using it; what he asked to SEE is now on screen.
 */
function CompanyOverviewTab({ rows, cards, ctx, includeEmpty }: {
  rows: MpItemStatusRow[] | null; cards: MpOverview | null; ctx: Ctx; includeEmpty: boolean
}) {
  const st = (o: { partial?: string | null } | null | undefined, name: string): Seen => ({ v2: ctx.v2, partial: partialOf(o).has(name) })
  const columns = useMemo<Column<MpItemStatusRow>[]>(() => [
    { key: 'company', sortKey: 'Company', header: 'Company', render: (r) => r.company },
    { key: 'item', sortKey: 'ItemNumber', header: 'Item Number', render: (r) => r.itemNumber },
    { key: 'product', sortKey: 'ProductName', header: 'Product Name', render: (r) => r.productName, clamp: 36 },
    { key: 'bu', sortKey: 'BusinessUnit', header: 'Business Unit', render: (r) => r.businessUnit },
    { key: 'supplier', sortKey: 'Supplier', header: 'Vendor', render: (r) => r.supplier, clamp: 28 },
    { key: 'poYtd', sortKey: 'PoQtyYtd', header: 'PO YTD', align: 'right', render: (r) => marked(r, 'poQtyYtd', num(r.poQtyYtd, dimOf(r.unit))), value: (r) => r.poQtyYtd ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('poQtyYtd') : undefined },
    { key: 'poPy', sortKey: 'PoQtyPY', header: 'PO PY', align: 'right', render: (r) => marked(r, 'poQtyPY', num(r.poQtyPY, dimOf(r.unit))), value: (r) => r.poQtyPY ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('poQtyPY') : undefined },
    { key: 'poDiff', sortKey: 'PoQtyDiff', header: 'Diff (PO)', align: 'right', render: (r) => marked(r, 'poQtyDiff', signed(r.poQtyDiff, dimOf(r.unit))) },
    { key: 'sold', sortKey: 'QtySold', header: 'Sold YTD', align: 'right', render: (r) => marked(r, 'qtySold', num(r.qtySold, dimOf(r.unit))), value: (r) => r.qtySold ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('qtySold') : undefined },
    { key: 'openSo', sortKey: 'OpenSoQty', header: 'Open SO YTD', align: 'right', render: (r) => marked(r, 'openSoQty', num(r.openSoQty, dimOf(r.unit))), value: (r) => r.openSoQty ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('openSoQty') : undefined },
    { key: 'soldPy', sortKey: 'QtySoldPY', header: 'Sold PY', align: 'right', render: (r) => marked(r, 'qtySoldPY', num(r.qtySoldPY, dimOf(r.unit))), value: (r) => r.qtySoldPY ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('qtySoldPY') : undefined },
    { key: 'netDiff', sortKey: 'NetSoldDiff', header: 'Diff (Sold + SOs − PY)', align: 'right', render: (r) => marked(r, 'netSoldDiff', signed(r.netSoldDiff, dimOf(r.unit))) },
    { key: 'onHand', sortKey: 'OnHand', header: 'On Hand', align: 'right', render: (r) => marked(r, 'onHand', num(r.onHand, dimOf(r.unit))), value: (r) => r.onHand ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('onHand') : undefined },
    { key: 'onOrder', sortKey: 'OnOrder', header: 'On Order', align: 'right', render: (r) => marked(r, 'onOrder', num(r.onOrder, dimOf(r.unit))), value: (r) => r.onOrder ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('onOrder') : undefined },
    /* The unit and currency are ALSO their own columns, because the extract
       needs them as data: a spreadsheet cell holding "12.4K kg" is text, and
       nobody can sum a column of text. On screen they are the suffix; in the
       file they are two more fields. */
    { key: 'unit', sortKey: 'Unit', header: 'Unit', render: (r) => r.unit },
    { key: 'currency', sortKey: 'CurrencyCode', header: 'Currency', render: (r) => currencyMark(r.currencyCode) },
  ], [ctx.v2, ctx.shown])

  const buildDoc = () => !rows?.length ? null : gridDoc<MpItemStatusRow>(ctx, 'mp-company', 'Company Overview', 'Company overview',
    'Purchases and sales per item, this year against last, in the order the business manager asked for.', [
    { header: 'Company', cell: (r) => r.company }, { header: 'Item Number', cell: (r) => r.itemNumber },
    { header: 'Product Name', cell: (r) => r.productName }, { header: 'Business Unit', cell: (r) => r.businessUnit },
    { header: 'Vendor', cell: (r) => r.supplier },
    { header: 'PO YTD', kind: 'num2', cell: (r) => r.poQtyYtd }, { header: 'PO PY', kind: 'num2', cell: (r) => r.poQtyPY },
    { header: 'Diff (PO)', kind: 'num2', cell: (r) => r.poQtyDiff }, { header: 'Sold YTD', kind: 'num2', cell: (r) => r.qtySold },
    { header: 'Open SO YTD', kind: 'num2', cell: (r) => r.openSoQty }, { header: 'Sold PY', kind: 'num2', cell: (r) => r.qtySoldPY },
    { header: 'Diff (Sold + SOs less PY)', kind: 'num2', cell: (r) => r.netSoldDiff },
    { header: 'On Hand', kind: 'num2', cell: (r) => r.onHand }, { header: 'On Order', kind: 'num2', cell: (r) => r.onOrder },
    { header: 'Unit', cell: (r) => r.unit }, { header: 'Currency', cell: (r) => r.currencyCode },
  ], rows, rows[0]?.totalRows, [includeEmpty ? 'Rows: every item' : 'Rows: items with stock, orders or sales'],
    'Diff (PO) is PO YTD less PO PY. Diff (Sold + SOs less PY) is quantity sold this year plus open sales orders, less quantity sold last year. Two different subtractions.')

  const u = dimOf(cards?.unit, cards?.unitCount)
  return (
    <>
      <CollapsibleStats storageKey="stats:material-planning:company">
        <div className="stat-strip">
          <StatTile surface="company" measureKey="cardSold" label="Sold YTD" value={numS(cards?.ytdQtySold, st(cards, 'ytdQtySold'))} fullValue={fullQty(cards?.ytdQtySold, st(cards, 'ytdQtySold'))} secondary={withDim('quantity, signed', u)} tone="accent" dense />
          <StatTile surface="company" measureKey="cardOnHand" label="Total on hand" value={numS(cards?.onHand, st(cards, 'onHand'))} fullValue={fullQty(cards?.onHand, st(cards, 'onHand'))} secondary={withDim(undefined, u)} dense />
          <StatTile surface="company" measureKey="cardPoQty" label="Total PO quantity" value={numS(cards?.poQtyYtd, st(cards, 'poQtyYtd'))} fullValue={fullQty(cards?.poQtyYtd, st(cards, 'poQtyYtd'))} secondary={withDim(`${numS(cards?.poQtyPY, st(cards, 'poQtyPY'))} last year`, u)} dense />
          <StatTile surface="company" measureKey="cardItems" label="Item count" value={(cards?.itemCount ?? 0).toLocaleString()} secondary="items in your selection" dense />
        </div>
      </CollapsibleStats>
      <ExpandableCard
        titleRepeatsTab
        breadcrumb={['Material Planning', 'Company Overview']}
        title="Company overview"
        subtitle="Purchases and sales per item, this year against last. Diff (PO) is PO YTD less PO PY; Diff (Sold + SOs − PY) is sold plus open sales orders, less last year."
        right={<ExportButtons build={buildDoc} />}
      >
        {!rows ? <Loading label="Loading the company overview…" />
          : rows.length === 0 ? <EmptyState title="No items to show" detail="Nothing matches this filter within what you are permitted to see." />
          : <DataGrid surface="company" gridId="mp-company" columns={columns} rows={rows} {...totalsOf(rows.map((r) => r.unit))} totalRows={rows[0]?.totalRows} sort={ctx.sort} onSortChange={ctx.onSortChange} fill />}
      </ExpandableCard>
    </>
  )
}

/* ------------------------------------------------------ inventory position */

/**
 * The manager's Inventory Position: his matrix rows (product, item group, item
 * number, vendor, warehouse), his column order, his Difference, and a bookmark
 * navigator that changes the GRAIN rather than the visual — so the reader keeps
 * their filters when they move between company, unit, team and product.
 *
 * ON ORDER OPENS. He asked for a tooltip on that column showing the PO number,
 * quantity and ETA behind it. It is a CLICK, not a hover: a hover tooltip does
 * not exist on touch, and the figure it explains is the one people question
 * most. The panel is a real region above the grid, so it can be read, exported
 * with the screen, and reached from the keyboard.
 */
function StockTab({ rows, cards, ctx, sortBy, groupBy, includeEmpty, refined, ordersFor, orders, ordersFailure, onOrders }: {
  rows: MpStockRow[] | null; cards: MpOverview | null; ctx: Ctx
  sortBy: MpStockSort; groupBy: MpStockGroupBy; includeEmpty: boolean; refined: boolean
  ordersFor: { item: string; company: string | null } | null
  orders: MpItemOrderRow[] | null
  ordersFailure: ApiFailure | null
  onOrders: (v: { item: string; company: string | null } | null) => void
}) {
  const st = (o: { partial?: string | null } | null | undefined, name: string): Seen => ({ v2: ctx.v2, partial: partialOf(o).has(name) })
  const atItem = groupBy === 'item' || groupBy === 'product'
  const columns = useMemo<Column<MpStockRow>[]>(() => [
    { key: 'company', sortKey: 'companyname', header: 'Company', render: (r) => r.companyName ?? r.company },
    /* The grouped column names itself after the grain. Skipped entirely at
       company grain, where the Company column beside it IS the group and two
       columns of the same name reads as a bug rather than as a rollup. */
    ...(groupBy === 'company' ? [] : [{
      key: 'group',
      sortKey: atItem ? 'productname' : 'grouplabel',
      header: atItem ? 'Product Name' : GROUP_LABEL[groupBy],
      render: (r: MpStockRow) => (atItem ? r.productName : r.groupLabel),
      // Tighter than the other grids on purpose: five identity columns come
      // before the manager's measures here, and every character they spend is
      // a character of Difference and On Order pushed off the first screen.
      // The full text stays on hover; the header is never truncated.
      clamp: 26,
    }]),
    /* DRAWN ONLY WHERE THEY CAN HOLD A VALUE (owner, 4 Oct 2026). Item Group,
       Item Number and Vendor belong to ONE item and Warehouse to one store, and
       a company, unit or team row adds up hundreds of items -- 229 a company,
       53 a unit, 28 a team on the clone -- so the procedure returns them empty
       there rather than naming one at random (027's note on Supplier). Four
       blank columns pushed On Order and Difference off the first screen. A
       "Product" row is one item over all its warehouses, so only Warehouse goes
       there. The reader's arrangement of a column not drawn waits, dormant in
       gridLayout, for the grouping that has it. */
    ...(atItem ? [
      { key: 'itemGroup', sortKey: 'itemgroup', header: 'Item Group', render: (r: MpStockRow) => r.itemGroup },
      { key: 'item', sortKey: 'itemnumber', header: 'Item Number', render: (r: MpStockRow) => r.itemNumber },
      { key: 'supplier', sortKey: 'supplier', header: 'Vendor', render: (r: MpStockRow) => r.supplier, clamp: 20 },
    ] : []),
    ...(groupBy === 'item' ? [{ key: 'warehouse', sortKey: 'warehouse', header: 'Warehouse', render: (r: MpStockRow) => r.warehouse }] : []),
    { key: 'onHand', sortKey: 'onhand', header: 'On Hand', align: 'right', render: (r) => marked(r, 'onHand', num(r.onHand, dimOf(r.unit, r.unitCount))), value: (r) => r.onHand ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('onHand') : undefined },
    { key: 'reserved', sortKey: 'reserved', header: 'Reserved Quantity', align: 'right', render: (r) => marked(r, 'reserved', num(r.reserved, dimOf(r.unit, r.unitCount))), value: (r) => r.reserved ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('reserved') : undefined },
    { key: 'physical', sortKey: 'physicalinv', header: 'Physical Quantity', align: 'right', render: (r) => marked(r, 'physicalInv', num(r.physicalInv, dimOf(r.unit, r.unitCount))), value: (r) => r.physicalInv ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('physicalInv') : undefined },
    { key: 'openSo', sortKey: 'opensoqty', header: 'Open SOs Quantity', align: 'right', render: (r) => marked(r, 'openSoQty', num(r.openSoQty, dimOf(r.unit, r.unitCount))), value: (r) => r.openSoQty ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('openSoQty') : undefined },
    {
      key: 'onOrder', sortKey: 'onorder', header: 'On Order', align: 'right',
      // The figure stays the figure; it just becomes reachable. Only where a
      // single item is on the row, because that is what the panel can answer.
      render: (r) => (r.onOrder === null || !r.itemNumber
        // Above item grain there is no panel to open, but the figure still
        // needs its unit — this branch had none, and read as a bare number
        // beside five columns that all carried one.
        ? marked(r, 'onOrder', num(r.onOrder, dimOf(r.unit, r.unitCount)))
        : (
          <button
            type="button"
            onClick={() => onOrders({ item: r.itemNumber!, company: r.company })}
            className="text-(--color-accent) hover:underline"
            title="Show the open orders behind this figure"
          >
            {numS(r.onOrder, st(r, 'onOrder'))}
            <Suffix d={dimOf(r.unit, r.unitCount)} />
          </button>
        )),
      value: (r) => r.onOrder ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('onOrder') : undefined,
    },
    { key: 'diff', sortKey: 'shortfall', header: 'Difference', align: 'right', render: (r) => marked(r, 'shortfall', shortOf(r.shortfall, dimOf(r.unit, r.unitCount))) },
    { key: 'value', sortKey: 'inventoryvalue', header: 'Inventory Value', align: 'right', render: (r) => marked(r, 'inventoryValue', cash(r.inventoryValue, curOf(r.currencyCode, r.currencyCount))), value: (r) => r.inventoryValue ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('inventoryValue') : undefined },
    /* Everything below was on the screen before the manager's notes and stays:
       his list is what he wants FIRST, not the whole of what anyone may read. */
    { key: 'bu', sortKey: 'businessunit', header: 'Business Unit', render: (r) => r.businessUnit },
    { key: 'team', sortKey: 'businessteamname', header: 'Business Team', render: (r) => r.businessTeamName ?? r.businessTeam },
    { key: 'position', sortKey: 'position', header: 'Position', align: 'right', render: (r) => marked(r, 'position', signed(r.position, dimOf(r.unit, r.unitCount))) },
    { key: 'min', sortKey: 'mininventory', header: 'Minimum', align: 'right', render: (r) => marked(r, 'minInventory', num(r.minInventory, dimOf(r.unit, r.unitCount))) },
    { key: 'openPo', sortKey: 'openpocount', header: 'Open POs', align: 'right', render: (r) => (atItem ? (r.openPoCount === null && ctx.v2 ? ABSENT : <>{r.openPoCount}</>) : NOT_RECORDED) },
    /* Pallets is a count OF pallets, so its unit is the column, not the row.
       Pallet Size is how much of the item fits on one, so its unit IS the
       row's — the two sit side by side and are deliberately not the same. */
    { key: 'pallets', sortKey: 'pallets', header: 'Pallets', align: 'right', render: (r) => (ctx.v2 && r.pallets === null && r.onHand !== null ? NOT_RECORDED : marked(r, 'pallets', num(r.pallets, dim('pallets')))), value: (r) => r.pallets ?? 0, total: true, withheld: ctx.v2 ? (r: MpStockRow) => (r.pallets === null ? (r.onHand === null ? 'withheld' as const : null) : partialOf(r).has('pallets') ? 'partial' as const : null) : undefined },
    { key: 'palletSize', sortKey: 'palletsize', header: 'Pallet Size', align: 'right', render: (r) => (r.palletSize === null && ctx.v2 ? governedBlank(ctx, 'palletSize') : marked(r, 'palletSize', num(r.palletSize, dimOf(r.unit, r.unitCount)))) },
    { key: 'lastMove', sortKey: 'lastmovement', header: 'Last Movement', render: (r) => date(r.lastMovement) },
    { key: 'days', sortKey: 'dayssincemovement', header: 'Days Since', align: 'right', render: (r) => (r.daysSinceMovement === null && ctx.v2 ? governedBlank(ctx, 'daysSinceMovement') : marked(r, 'daysSinceMovement', num(r.daysSinceMovement, dim('days')))) },
    { key: 'cost', sortKey: 'inventorycost', header: 'Inventory Cost', align: 'right', render: (r) => marked(r, 'inventoryCost', cash(r.inventoryCost, curOf(r.currencyCode, r.currencyCount))), value: (r) => r.inventoryCost ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('inventoryCost') : undefined },
    { key: 'unitCost', sortKey: 'unitcost', header: 'Unit Cost', align: 'right', render: (r) => marked(r, 'unitCost', cash(r.unitCost, curOf(r.currencyCode, r.currencyCount))) },
    // groupBy, not just atItem: the grouped column renames itself per grain,
    // and memoising on atItem alone left the Business unit and Business team
    // grains showing the column computed for Company — which is to say, none.
  ], [atItem, groupBy, onOrders, ctx.v2, ctx.shown])

  const buildDoc = () => !rows?.length ? null : gridDoc<MpStockRow>(ctx, 'mp-stock', 'Inventory Position', 'Inventory position',
    `Grouped by ${GROUP_LABEL[groupBy].toLowerCase()}.`, [
    { header: 'Company', cell: (r) => r.companyName ?? r.company },
    /* The screen's identity columns, grouping for grouping. This used to carry
       all five everywhere, so a unit or team extract had a "Product Name"
       column holding the unit's or team's name. */
    ...(groupBy === 'company' ? [] : [{ header: atItem ? 'Product Name' : GROUP_LABEL[groupBy], cell: (r: MpStockRow) => (atItem ? r.productName : r.groupLabel) }]),
    ...(atItem ? [
      { header: 'Item Group', cell: (r: MpStockRow) => r.itemGroup },
      { header: 'Item Number', cell: (r: MpStockRow) => r.itemNumber },
      { header: 'Vendor', cell: (r: MpStockRow) => r.supplier },
    ] : []),
    ...(groupBy === 'item' ? [{ header: 'Warehouse', cell: (r: MpStockRow) => r.warehouse }] : []),
    { header: 'On Hand', kind: 'num2', cell: (r) => r.onHand }, { header: 'Reserved Quantity', kind: 'num2', cell: (r) => r.reserved },
    { header: 'Physical Quantity', kind: 'num2', cell: (r) => r.physicalInv }, { header: 'Open SOs Quantity', kind: 'num2', cell: (r) => r.openSoQty },
    { header: 'On Order', kind: 'num2', cell: (r) => r.onOrder }, { header: 'Difference', kind: 'num2', cell: (r) => r.shortfall },
    { header: 'Inventory Value', kind: 'money', cell: (r) => r.inventoryValue },
    { header: 'Business Unit', cell: (r) => r.businessUnit }, { header: 'Business Team', cell: (r) => r.businessTeamName ?? r.businessTeam },
    { header: 'Position', kind: 'num2', cell: (r) => r.position }, { header: 'Minimum', kind: 'num2', cell: (r) => r.minInventory },
    { header: 'Open POs', kind: 'int', cell: (r) => (atItem ? r.openPoCount : null) }, { header: 'Pallets', kind: 'num2', cell: (r) => r.pallets },
    { header: 'Pallet Size', kind: 'num2', cell: (r) => r.palletSize }, { header: 'Last Movement', cell: (r) => dateS(r.lastMovement) },
    { header: 'Days Since', kind: 'int', cell: (r) => r.daysSinceMovement },
    { header: 'Inventory Cost', kind: 'money', cell: (r) => r.inventoryCost }, { header: 'Unit Cost', kind: 'money', cell: (r) => r.unitCost },
    { header: 'Unit', cell: (r) => dimOf(r.unit, r.unitCount)?.text ?? null },
    { header: 'Currency', cell: (r) => curOf(r.currencyCode, r.currencyCount)?.text ?? null },
  ], rows, rows[0]?.totalRows,
    [`Grouped by: ${GROUP_LABEL[groupBy]}`, `Sorted by: ${SORT_LABEL[sortBy]}`,
      includeEmpty ? 'Rows: every item, including those with no inventory record' : 'Rows: items with stock or movement'],
    'Difference is open sales orders less what is on hand and on order: a POSITIVE number is demand that stock cannot meet. Position beside it is the old measure and runs the other way — it ADDS what is on order.')

  const cardUnit = dimOf(cards?.unit, cards?.unitCount)
  const cardCur = curOf(cards?.currencyCode, cards?.currencyCount)
  return (
    <>
      <CollapsibleStats storageKey="stats:material-planning:position">
        <div className="stat-strip">
          <StatTile surface="stock" measureKey="cardReserved" label="Total reserved" value={numS(cards?.openSoQty, st(cards, 'openSoQty'))} fullValue={fullQty(cards?.openSoQty, st(cards, 'openSoQty'))} secondary={withDim('open sales orders', cardUnit)} tone="accent" dense />
          <StatTile surface="stock" measureKey="cardOnHand" label="Total on hand" value={numS(cards?.onHand, st(cards, 'onHand'))} fullValue={fullQty(cards?.onHand, st(cards, 'onHand'))} secondary={withDim(undefined, cardUnit)} dense />
          <StatTile surface="stock" measureKey="cardValue" label="Inventory value" value={cashS(cards?.inventoryValue, st(cards, 'inventoryValue'))} fullValue={fullCash(cards?.inventoryValue, st(cards, 'inventoryValue'))} secondary={withCur(undefined, cardCur)} dense />
          <StatTile surface="stock" measureKey="cardItems" label="Item count" value={(cards?.itemCount ?? 0).toLocaleString()} secondary="items in your selection" dense />
        </div>
      </CollapsibleStats>
      {refined ? (
        <p className="text-(length:--text-small) text-(--color-ink-3)">
          The cards answer the company, unit, group and supplier filters. The warehouse and the search box refine the grid below only.
        </p>
      ) : null}

      {ordersFor ? <ItemOrdersPanel item={ordersFor.item} rows={orders} failure={ordersFailure} onClose={() => onOrders(null)} /> : null}

      <ExpandableCard
        titleRepeatsTab
        breadcrumb={['Material Planning', 'Inventory Position']}
        title={`Inventory position by ${GROUP_LABEL[groupBy].toLowerCase()}`}
        subtitle="Difference is open sales orders less on hand and on order — positive means short. On Order opens the orders behind it."
        right={<ExportButtons build={buildDoc} />}
      >
        {!rows ? <Loading label="Loading stock…" />
          : rows.length === 0 ? <EmptyState title="Nothing in stock to show" detail="No item matches this filter within what you are permitted to see." />
          : <DataGrid surface="stock" gridId="mp-stock" columns={columns} rows={rows} {...totalsOf(rows.map((r) => dimOf(r.unit, r.unitCount)?.text))} totalRows={rows[0]?.totalRows} sort={ctx.sort} onSortChange={ctx.onSortChange} fill />}
      </ExpandableCard>
    </>
  )
}

/** The On Order panel: the open orders behind one item's figure. */
function ItemOrdersPanel({ item, rows, failure, onClose }: {
  item: string; rows: MpItemOrderRow[] | null; failure: ApiFailure | null; onClose: () => void
}) {
  return (
    <Card>
      <CardHeader
        title={`On order — ${item}`}
        subtitle="Purchase-order lines still live for this item, soonest arrival first."
        right={<Button onClick={onClose}>Close</Button>}
      />
      {/* SAID OUT LOUD, because the two numbers will not always agree. On Order
          is the planning view's own column; these lines come from purchasing.
          Different tables, no line-by-line reconciliation under any definition
          of open — so the panel explains itself rather than letting a reader
          add the lines up and conclude the report is wrong. */}
      <p className="px-4 pt-3 text-(length:--text-small) text-(--color-ink-3)">
        These lines come from purchasing; the On Order figure comes from the planning view. They answer the same
        question from two systems and need not add up exactly.
      </p>
      {/* A failed read shows HERE, inside the panel it belongs to; Close
          dismisses it and the grid underneath is untouched. */}
      {failure ? <FailureState failure={failure} />
        : !rows ? <Loading label="Loading orders…" />
        : rows.length === 0
          ? <EmptyState title="No live orders" detail="No purchase-order line is still open for this item within what you are permitted to see." />
          : (
            <div className="overflow-x-auto p-4">
              <table className="report-table w-full text-(length:--text-small)">
                <thead>
                  <tr className="text-center text-(--color-ink-3)">
                    <th className="text-center pr-4 pb-1 font-semibold">PO Number</th>
                    <th className="text-center pr-4 pb-1 font-semibold">Line</th>
                    <th className="text-center pr-4 pb-1 font-semibold">Company</th>
                    <th className="text-center pr-4 pb-1 font-semibold">Vendor</th>
                    <th className="pr-4 pb-1 text-center font-semibold">Quantity</th>
                    <th className="pr-4 pb-1 text-center font-semibold">Remaining</th>
                    <th className="text-center pr-4 pb-1 font-semibold">ETA</th>
                    <th className="pb-1 text-center font-semibold">PO Value</th>
                  </tr>
                </thead>
                {/* The separator is on the CELL, not the row: a border on the
                    row is clipped by any cell that sits on its own layer, and
                    the line vanishes under exactly the column that never
                    scrolls away. */}
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.poNumber}|${r.lineNumber}`} className="[&>td]:border-t [&>td]:border-(--color-line) [&>td]:py-1">
                      <td className="pr-4 font-semibold text-(--color-ink)">{r.poNumber}</td>
                      <td className="pr-4 tabular-nums text-(--color-ink-2)">{r.lineNumber ?? ''}</td>
                      <td className="pr-4 text-(--color-ink-2)">{r.company}</td>
                      <td className="pr-4 text-(--color-ink-2)">{r.supplier}</td>
                      <td className="pr-4 text-right tabular-nums text-(--color-ink)">{marked(r, 'quantity', num(r.quantity, dimOf(r.unit)))}</td>
                      <td className="pr-4 text-right tabular-nums text-(--color-ink)">{marked(r, 'stillDue', num(r.stillDue, dimOf(r.unit)))}</td>
                      <td className="pr-4 text-(--color-ink-2)">{date(r.etaDate)}</td>
                      <td className="text-right tabular-nums text-(--color-ink)">{marked(r, 'lineAmount', cash(r.lineAmount, curOf(r.currencyCode)))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
    </Card>
  )
}

/* ---------------------------------------------------------- purchase orders */

function OrdersTab({ rows, status, onStatus, ctx }: {
  rows: MpPurchaseOrderRow[] | null; status: MpOrderStatus; onStatus: (s: MpOrderStatus) => void; ctx: Ctx
}) {
  const columns = useMemo<Column<MpPurchaseOrderRow>[]>(() => [
    { key: 'po', sortKey: 'PoNumber', header: 'PO Number', render: (r) => r.poNumber, stickyLeft: true },
    { key: 'line', sortKey: 'LineNumber', header: 'Line', align: 'right', render: (r) => <>{r.lineNumber ?? ''}</> },
    { key: 'item', sortKey: 'ItemId', header: 'Item', render: (r) => r.itemId },
    { key: 'name', sortKey: 'ItemName', header: 'Description', render: (r) => r.itemName, clamp: 36 },
    { key: 'supplier', sortKey: 'Supplier', header: 'Supplier', render: (r) => r.supplier, clamp: 28 },
    { key: 'qty', sortKey: 'Quantity', header: 'Quantity', align: 'right', render: (r) => marked(r, 'quantity', num(r.quantity, dimOf(r.unit))), value: (r) => r.quantity ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('quantity') : undefined },
    { key: 'due', sortKey: 'StillDue', header: 'Still Due', align: 'right', render: (r) => marked(r, 'stillDue', num(r.stillDue, dimOf(r.unit))) },
    { key: 'unit', sortKey: 'Unit', header: 'Unit', render: (r) => r.unit },
    { key: 'company', sortKey: 'Company', header: 'Company', render: (r) => r.company },
    { key: 'site', sortKey: 'Site', header: 'Site', render: (r) => r.site },
    { key: 'category', sortKey: 'SupplierCategory', header: 'Supplier Category', render: (r) => r.supplierCategory },
    { key: 'poDate', sortKey: 'PoDate', header: 'PO Date', render: (r) => date(r.poDate) },
    { key: 'delivery', sortKey: 'DeliveryDate', header: 'Delivery Date', render: (r) => date(r.deliveryDate) },
    { key: 'readiness', sortKey: 'ReadinessDate', header: 'Readiness', render: (r) => date(r.readinessDate) },
    { key: 'etd', sortKey: 'EtdDate', header: 'ETD', render: (r) => date(r.etdDate) },
    { key: 'eta', sortKey: 'EtaDate', header: 'ETA', render: (r) => date(r.etaDate) },
    { key: 'ship', sortKey: 'ShipDate', header: 'Ship Date', render: (r) => date(r.shipDate) },
    { key: 'ship2', sortKey: 'ShipDate2', header: 'Ship Date 2', render: (r) => date(r.shipDate2) },
    { key: 'grn', sortKey: 'GrnDate', header: 'Goods Received', render: (r) => date(r.grnDate) },
    { key: 'lineStatus', sortKey: 'LineStatusCode', header: 'Line Status', render: (r) => (r.lineStatusCode === null ? NOT_RECORDED : <>{LINE_STATUS[r.lineStatusCode] ?? `Code ${r.lineStatusCode}`}</>) },
    { key: 'approval', sortKey: 'ApprovalCode', header: 'Approval', render: (r) => approval(r.approvalCode) },
    { key: 'orderStatus', sortKey: 'OrderStatus', header: 'Order Status', render: (r) => r.orderStatus },
    { key: 'orderProgress', sortKey: 'OrderProgress', header: 'Voyage Status', render: (r) => r.orderProgress, clamp: 24 },
    { key: 'shipStatus', sortKey: 'ShipmentStatus', header: 'Shipment Status', render: (r) => r.shipmentStatus },
    { key: 'mode', sortKey: 'ModeOfDelivery', header: 'Mode of Delivery', render: (r) => r.modeOfDelivery },
    { key: 'terms', sortKey: 'DeliveryTerms', header: 'Delivery Terms', render: (r) => r.deliveryTerms },
    { key: 'mawb', sortKey: 'Mawb', header: 'Air Waybill', render: (r) => r.mawb },
    { key: 'responsible', sortKey: 'Responsible', header: 'Responsible', render: (r) => r.responsible, clamp: 24 },
    { key: 'fileClosed', sortKey: 'FileClosed', header: 'File Closed', render: (r) => (r.fileClosed === null ? NOT_RECORDED : <>{r.fileClosed ? 'Yes' : 'No'}</>) },
    { key: 'currency', sortKey: 'CurrencyCode', header: 'Currency', render: (r) => currencyMark(r.currencyCode) },
    { key: 'price', sortKey: 'UnitPrice', header: 'Unit Price', align: 'right', render: (r) => marked(r, 'unitPrice', cash(r.unitPrice, curOf(r.currencyCode))) },
    { key: 'basis', sortKey: 'PriceBasis', header: 'Price Basis', align: 'right', render: (r) => marked(r, 'priceBasis', num(r.priceBasis, dimOf(r.unit))) },
    { key: 'amount', sortKey: 'LineAmount', header: 'Line Amount', align: 'right', render: (r) => marked(r, 'lineAmount', cash(r.lineAmount, curOf(r.currencyCode))), value: (r) => r.lineAmount ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('lineAmount') : undefined },
  ], [ctx.v2, ctx.shown])

  const buildDoc = () => !rows?.length ? null : gridDoc<MpPurchaseOrderRow>(ctx, 'mp-purchase', 'Purchase Orders','Purchase orders',
    'Open means line status 1 or 2 — the order pages’ definition; the Overview tile counts by file flag.', [
    { header: 'PO Number', cell: (r) => r.poNumber }, { header: 'Line', kind: 'int', cell: (r) => r.lineNumber },
    { header: 'Item', cell: (r) => r.itemId }, { header: 'Description', cell: (r) => r.itemName }, { header: 'Supplier', cell: (r) => r.supplier },
    { header: 'Quantity', kind: 'num2', cell: (r) => r.quantity }, { header: 'Still Due', kind: 'num2', cell: (r) => r.stillDue },
    { header: 'Unit', cell: (r) => r.unit },
    { header: 'Company', cell: (r) => r.company }, { header: 'Site', cell: (r) => r.site }, { header: 'Supplier Category', cell: (r) => r.supplierCategory },
    { header: 'PO Date', cell: (r) => dateS(r.poDate) }, { header: 'Delivery Date', cell: (r) => dateS(r.deliveryDate) },
    { header: 'Readiness', cell: (r) => dateS(r.readinessDate) }, { header: 'ETD', cell: (r) => dateS(r.etdDate) }, { header: 'ETA', cell: (r) => dateS(r.etaDate) },
    { header: 'Ship Date', cell: (r) => dateS(r.shipDate) }, { header: 'Ship Date 2', cell: (r) => dateS(r.shipDate2) }, { header: 'Goods Received', cell: (r) => dateS(r.grnDate) },
    { header: 'Line Status', cell: (r) => (r.lineStatusCode === null ? null : LINE_STATUS[r.lineStatusCode] ?? `Code ${r.lineStatusCode}`) },
    { header: 'Approval', cell: (r) => approvalS(r.approvalCode) }, { header: 'Order Status', cell: (r) => r.orderStatus },
    { header: 'Voyage Status', cell: (r) => r.orderProgress },
    { header: 'Shipment Status', cell: (r) => r.shipmentStatus }, { header: 'Mode of Delivery', cell: (r) => r.modeOfDelivery },
    { header: 'Delivery Terms', cell: (r) => r.deliveryTerms }, { header: 'Air Waybill', cell: (r) => r.mawb }, { header: 'Responsible', cell: (r) => r.responsible },
    { header: 'File Closed', cell: (r) => (r.fileClosed === null ? null : r.fileClosed ? 'Yes' : 'No') }, { header: 'Currency', cell: (r) => r.currencyCode },
    { header: 'Unit Price', kind: 'money', cell: (r) => r.unitPrice }, { header: 'Price Basis', kind: 'num2', cell: (r) => r.priceBasis },
    { header: 'Line Amount', kind: 'money', cell: (r) => r.lineAmount },
  ], rows, rows[0]?.totalRows, [`Status: ${STATUS_LABEL[status]}`])

  return (
    <ExpandableCard
      titleRepeatsTab
      breadcrumb={['Material Planning', 'Purchase Orders']}
      title="Purchase orders"
      subtitle="Open means the line is still commercially live, whatever its approval state."
      right={
          <div className="flex items-center gap-3">
            <Segmented
              label="Status"
              options={[
                { value: 'open', label: 'Open', title: 'Line status 1 or 2 — still live' },
                { value: 'review', label: 'Under review', title: 'Live, and awaiting approval' },
                { value: 'closed', label: 'Closed', title: 'Line status 3 — finished' },
                { value: 'cancelled', label: 'Cancelled', title: 'Line status 4' },
                { value: 'all', label: 'All', title: 'Every line, whatever its status' },
              ]}
              value={status}
              onChange={onStatus}
            />
            <ExportButtons build={buildDoc} />
          </div>
      }
    >
      {!rows ? <Loading label="Loading purchase orders…" />
        : rows.length === 0 ? <EmptyState title="No purchase orders to show" detail="Nothing matches this status within what you are permitted to see." />
        : <DataGrid surface="purchase" gridId="mp-purchase" columns={columns} rows={rows} {...allLineTotals(rows, ctx.v2)} totalRows={rows[0]?.totalRows} sort={ctx.sort} onSortChange={ctx.onSortChange} fill />}
    </ExpandableCard>
  )
}

/* --------------------------------------------------------------- shipments */

/** What each of the three shipment screens is, in the words the screen uses. */
const SHIPMENT_COPY: Record<MpShipmentView, { page: string; title: string; subtitle: string; empty: string; note?: string }> = {
  tracker: {
    page: 'Shipment Tracker',
    title: 'Shipment tracker',
    subtitle: 'Confirmed open lines still to arrive — line status 1, approval 40, order status Open — newest readiness first.',
    empty: 'No confirmed open line matches this filter within what you are permitted to see.',
  },
  'arriving-soon': {
    page: 'Arriving Soon',
    title: 'Arriving soon',
    subtitle: 'Lines whose ETA falls between today and thirty days from today.',
    empty: 'Nothing is due to arrive in the next thirty days within what you are permitted to see.',
    note: 'ETA on or after today and on or before today plus thirty days, on lines still open or received.',
  },
  late: {
    page: 'Late Shipments',
    title: 'Late shipments',
    subtitle: 'An ETA already past with no goods receipt recorded.',
    empty: 'Nothing is overdue within what you are permitted to see.',
    // The manager's rule as literally written matches nothing, and saying so
    // on the screen is better than quietly answering a different question.
    note: 'The request read "ETA < Today and Shipment Status = Received". Received is an order status, not a shipment status, and that literal condition returns nothing at all in the source. This page reads the rule as what it means: an ETA in the past with nothing received yet.',
  },
}

function ShipmentsTab({ rows, ctx, view, exportRef }: {
  rows: MpShipmentRow[] | null; ctx: Ctx; view: MpShipmentView
  /** Filled in below so the page can render EXTRACT in the filter bar. */
  exportRef?: { current: (() => ExportDoc | null) | null }
}) {
  const copy = SHIPMENT_COPY[view]
  /* THE MANAGER'S COLUMN ORDER, in his words with ours in brackets where the
     source calls it something else: Company, PO No., Item No., Product Name,
     Vendor, Quantity, Remaining Quantity, PO Date, Pick-up Date (readiness,
     recorded on 3.7% of lines), ETS (the ETD column), Ship Date, ETA, PO Value,
     PO Status (Order_Status), Shipment Status, Voyage Status (the shipment's SHIPSTATUSID),
     Mode, Orderer (Created_By). Everything after that was already on the
     screen and stays. */
  const columns = useMemo<Column<MpShipmentRow>[]>(() => [
    { key: 'company', sortKey: 'Company', header: 'Company', render: (r) => r.company },
    { key: 'po', sortKey: 'PoNumber', header: 'PO No.', render: (r) => r.poNumber },
    { key: 'item', sortKey: 'ItemId', header: 'Item No.', render: (r) => r.itemId },
    { key: 'name', sortKey: 'ItemName', header: 'Product Name', render: (r) => r.itemName, clamp: 36 },
    { key: 'supplier', sortKey: 'Supplier', header: 'Vendor', render: (r) => r.supplier, clamp: 28 },
    { key: 'qty', sortKey: 'Quantity', header: 'Quantity', align: 'right', render: (r) => marked(r, 'quantity', num(r.quantity, dimOf(r.unit))), value: (r) => r.quantity ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('quantity') : undefined },
    { key: 'due', sortKey: 'StillDue', header: 'Remaining Quantity', align: 'right', render: (r) => marked(r, 'stillDue', num(r.stillDue, dimOf(r.unit))) },
    { key: 'unit', sortKey: 'Unit', header: 'Unit', render: (r) => r.unit },
    { key: 'poDate', sortKey: 'PoDate', header: 'PO Date', render: (r) => date(r.poDate) },
    { key: 'readiness', sortKey: 'ReadinessDate', header: 'Pick-up Date', render: (r) => date(r.readinessDate) },
    { key: 'etd', sortKey: 'EtdDate', header: 'ETS', render: (r) => date(r.etdDate) },
    { key: 'ship', sortKey: 'ShipDate', header: 'Ship Date', render: (r) => date(r.shipDate) },
    { key: 'eta', sortKey: 'EtaDate', header: 'ETA', render: (r) => date(r.etaDate) },
    { key: 'amount', sortKey: 'LineAmount', header: 'PO Value', align: 'right', render: (r) => marked(r, 'lineAmount', cash(r.lineAmount, curOf(r.currencyCode))), value: (r) => r.lineAmount ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('lineAmount') : undefined },
    { key: 'orderStatus', sortKey: 'OrderStatus', header: 'PO Status', render: (r) => r.orderStatus },
    { key: 'shipStatus', sortKey: 'ShipmentStatus', header: 'Shipment Status', render: (r) => r.shipmentStatus },
    { key: 'orderProgress', sortKey: 'OrderProgress', header: 'Voyage Status', render: (r) => r.orderProgress, clamp: 24 },
    { key: 'mode', sortKey: 'ModeOfDelivery', header: 'Mode', render: (r) => r.modeOfDelivery },
    { key: 'orderer', sortKey: 'Orderer', header: 'Orderer', render: (r) => r.orderer, clamp: 24 },
    /* Below the manager's list, and kept: his order says what he wants first,
       not what anyone else may stop being able to see. */
    { key: 'ship2', sortKey: 'ShipDate2', header: 'Ship Date 2', render: (r) => date(r.shipDate2) },
    { key: 'grn', sortKey: 'GrnDate', header: 'Goods Received', render: (r) => date(r.grnDate) },
    { key: 'mawb', sortKey: 'Mawb', header: 'Air Waybill', render: (r) => r.mawb },
    { key: 'terms', sortKey: 'DeliveryTerms', header: 'Delivery Terms', render: (r) => r.deliveryTerms },
    { key: 'site', sortKey: 'Site', header: 'Site', render: (r) => r.site },
    { key: 'approval', sortKey: 'ApprovalCode', header: 'Approval', render: (r) => approval(r.approvalCode) },
    { key: 'lineStatus', sortKey: 'LineStatusCode', header: 'Line Status', render: (r) => (r.lineStatusCode === null ? NOT_RECORDED : <>{LINE_STATUS[r.lineStatusCode] ?? `Code ${r.lineStatusCode}`}</>) },
    { key: 'responsible', sortKey: 'Responsible', header: 'Responsible', render: (r) => r.responsible, clamp: 24 },
    { key: 'currency', sortKey: 'CurrencyCode', header: 'Currency', render: (r) => currencyMark(r.currencyCode) },
    { key: 'price', sortKey: 'UnitPrice', header: 'Unit Price', align: 'right', render: (r) => marked(r, 'unitPrice', cash(r.unitPrice, curOf(r.currencyCode))) },
    { key: 'basis', sortKey: 'PriceBasis', header: 'Price Basis', align: 'right', render: (r) => marked(r, 'priceBasis', num(r.priceBasis, dimOf(r.unit))) },
  ], [ctx.v2, ctx.shown])

  const buildDoc = () => !rows?.length ? null : gridDoc<MpShipmentRow>(ctx, 'mp-shipments', copy.page, copy.title, copy.subtitle, [
    { header: 'Company', cell: (r) => r.company }, { header: 'PO No.', cell: (r) => r.poNumber },
    { header: 'Item No.', cell: (r) => r.itemId }, { header: 'Product Name', cell: (r) => r.itemName },
    { header: 'Vendor', cell: (r) => r.supplier }, { header: 'Quantity', kind: 'num2', cell: (r) => r.quantity },
    { header: 'Remaining Quantity', kind: 'num2', cell: (r) => r.stillDue }, { header: 'Unit', cell: (r) => r.unit },
    { header: 'PO Date', cell: (r) => dateS(r.poDate) },
    { header: 'Pick-up Date', cell: (r) => dateS(r.readinessDate) }, { header: 'ETS', cell: (r) => dateS(r.etdDate) },
    { header: 'Ship Date', cell: (r) => dateS(r.shipDate) }, { header: 'ETA', cell: (r) => dateS(r.etaDate) },
    { header: 'PO Value', kind: 'money', cell: (r) => r.lineAmount }, { header: 'PO Status', cell: (r) => r.orderStatus },
    { header: 'Shipment Status', cell: (r) => r.shipmentStatus }, { header: 'Voyage Status', cell: (r) => r.orderProgress },
    { header: 'Mode', cell: (r) => r.modeOfDelivery }, { header: 'Orderer', cell: (r) => r.orderer },
    { header: 'Ship Date 2', cell: (r) => dateS(r.shipDate2) }, { header: 'Goods Received', cell: (r) => dateS(r.grnDate) },
    { header: 'Air Waybill', cell: (r) => r.mawb }, { header: 'Delivery Terms', cell: (r) => r.deliveryTerms },
    { header: 'Site', cell: (r) => r.site }, { header: 'Approval', cell: (r) => approvalS(r.approvalCode) },
    { header: 'Line Status', cell: (r) => (r.lineStatusCode === null ? null : LINE_STATUS[r.lineStatusCode] ?? `Code ${r.lineStatusCode}`) },
    { header: 'Responsible', cell: (r) => r.responsible }, { header: 'Currency', cell: (r) => r.currencyCode },
    { header: 'Unit Price', kind: 'money', cell: (r) => r.unitPrice }, { header: 'Price Basis', kind: 'num2', cell: (r) => r.priceBasis },
  ], rows, rows[0]?.totalRows, [], copy.note ?? pageNote(ctx, rows.length, rows[0]?.totalRows))

  if (exportRef) exportRef.current = buildDoc
  if (!rows) return <Loading label={`Loading ${copy.title.toLowerCase()}…`} />
  return (
    <ExpandableCard
      titleRepeatsTab
      breadcrumb={['Material Planning', copy.title]}
      title={copy.title}
      subtitle={copy.subtitle}
      right={<ExportButtons build={buildDoc} />}
    >
      {view === 'late' ? (
        <p className="px-4 pt-3 text-(length:--text-small) text-(--color-ink-3)">{SHIPMENT_COPY.late.note}</p>
      ) : null}
      {rows.length === 0
        ? <EmptyState title="Nothing to show" detail={copy.empty} />
        : <DataGrid surface="shipments" gridId="mp-shipments" columns={columns} rows={rows} {...allLineTotals(rows, ctx.v2)} totalRows={rows[0]?.totalRows} sort={ctx.sort} onSortChange={ctx.onSortChange} fill />}
    </ExpandableCard>
  )
}

/* ------------------------------------------------------------- item status */

function ItemsTab({ rows, summary, selected, onSelect, ctx, includeEmpty }: {
  rows: MpItemStatusRow[] | null; summary: MpItemSummary | null; selected: string
  onSelect: (item: string) => void; ctx: Ctx; includeEmpty: boolean
}) {
  const st = (o: { partial?: string | null } | null | undefined, name: string): Seen => ({ v2: ctx.v2, partial: partialOf(o).has(name) })
  const columns = useMemo<Column<MpItemStatusRow>[]>(() => [
    {
      key: 'pick', header: 'Summary',
      render: (r) => (
        <button
          type="button"
          onClick={() => onSelect(r.itemNumber ?? '')}
          className={`text-(length:--text-small) font-semibold ${r.itemNumber === selected ? 'text-(--color-ink-3)' : 'text-(--color-accent) hover:underline'}`}
        >
          {r.itemNumber === selected ? 'shown' : 'view'}
        </button>
      ),
    },
    { key: 'item', sortKey: 'ItemNumber', header: 'Item Number', render: (r) => r.itemNumber },
    { key: 'product', sortKey: 'ProductName', header: 'Product', render: (r) => r.productName, clamp: 36 },
    { key: 'bu', sortKey: 'BusinessUnit', header: 'Business Unit', render: (r) => r.businessUnit },
    { key: 'group', sortKey: 'ItemGroup', header: 'Item Group', render: (r) => r.itemGroup },
    { key: 'supplier', sortKey: 'Supplier', header: 'Supplier', render: (r) => r.supplier, clamp: 28 },
    { key: 'unit', sortKey: 'Unit', header: 'Unit', render: (r) => r.unit },
    { key: 'onHand', sortKey: 'OnHand', header: 'On Hand', align: 'right', render: (r) => marked(r, 'onHand', num(r.onHand, dimOf(r.unit))), value: (r) => r.onHand ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('onHand') : undefined },
    { key: 'onOrder', sortKey: 'OnOrder', header: 'On Order', align: 'right', render: (r) => marked(r, 'onOrder', num(r.onOrder, dimOf(r.unit))) },
    { key: 'reserved', sortKey: 'Reserved', header: 'Reserved', align: 'right', render: (r) => marked(r, 'reserved', num(r.reserved, dimOf(r.unit))) },
    { key: 'poQty', sortKey: 'PoQty', header: 'On PO', align: 'right', render: (r) => marked(r, 'poQty', num(r.poQty, dimOf(r.unit))) },
    { key: 'openPo', sortKey: 'OpenPoCount', header: 'Open POs', align: 'right', render: (r) => <>{r.openPoCount}</> },
    { key: 'min', sortKey: 'MinInventory', header: 'Minimum', align: 'right', render: (r) => marked(r, 'minInventory', num(r.minInventory, dimOf(r.unit))) },
    { key: 'below', sortKey: 'BelowMinimum', header: 'Above / Below Min', align: 'right', render: (r) => marked(r, 'belowMinimum', signed(r.belowMinimum, dimOf(r.unit))) },
    { key: 'sold', sortKey: 'QtySold', header: 'Sold', align: 'right', render: (r) => marked(r, 'qtySold', num(r.qtySold, dimOf(r.unit))) },
    { key: 'soldPY', sortKey: 'QtySoldPY', header: 'Sold Last Year', align: 'right', render: (r) => marked(r, 'qtySoldPY', num(r.qtySoldPY, dimOf(r.unit))) },
    { key: 'diff', sortKey: 'QtyDiff', header: 'Difference', align: 'right', render: (r) => marked(r, 'qtyDiff', signed(r.qtyDiff, dimOf(r.unit))) },
    { key: 'currency', sortKey: 'CurrencyCode', header: 'Currency', render: (r) => currencyMark(r.currencyCode) },
    { key: 'cost', sortKey: 'InventoryCost', header: 'Inventory Cost', align: 'right', render: (r) => marked(r, 'inventoryCost', cash(r.inventoryCost, curOf(r.currencyCode))), value: (r) => r.inventoryCost ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('inventoryCost') : undefined },
    { key: 'value', sortKey: 'InventoryValue', header: 'Inventory Value', align: 'right', render: (r) => marked(r, 'inventoryValue', cash(r.inventoryValue, curOf(r.currencyCode))), value: (r) => r.inventoryValue ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('inventoryValue') : undefined },
  ], [onSelect, selected, ctx.v2, ctx.shown])

  const buildDoc = (): ExportDoc | null => {
    if (!rows?.length) return null
    const doc = gridDoc<MpItemStatusRow>(ctx, 'mp-items', 'Item Status', 'Item status', 'One row per item; the summary above answers for the selection.', [
      { header: 'Item Number', cell: (r) => r.itemNumber }, { header: 'Product', cell: (r) => r.productName }, { header: 'Business Unit', cell: (r) => r.businessUnit },
      { header: 'Item Group', cell: (r) => r.itemGroup }, { header: 'Supplier', cell: (r) => r.supplier },
      { header: 'On Hand', kind: 'num2', cell: (r) => r.onHand }, { header: 'On Order', kind: 'num2', cell: (r) => r.onOrder }, { header: 'Reserved', kind: 'num2', cell: (r) => r.reserved },
      { header: 'On PO', kind: 'num2', cell: (r) => r.poQty }, { header: 'Open POs', kind: 'int', cell: (r) => r.openPoCount }, { header: 'Minimum', kind: 'num2', cell: (r) => r.minInventory },
      { header: 'Above / Below Min', kind: 'num2', cell: (r) => r.belowMinimum }, { header: 'Sold', kind: 'num2', cell: (r) => r.qtySold },
      { header: 'Sold Last Year', kind: 'num2', cell: (r) => r.qtySoldPY }, { header: 'Difference', kind: 'num2', cell: (r) => r.qtyDiff },
      { header: 'Inventory Cost', kind: 'money', cell: (r) => r.inventoryCost }, { header: 'Inventory Value', kind: 'money', cell: (r) => r.inventoryValue },
      { header: 'Unit', cell: (r) => r.unit }, { header: 'Currency', cell: (r) => r.currencyCode },
    ], rows, rows[0]?.totalRows, [selected ? `Item: ${selected}` : 'Item: all in selection', includeEmpty ? 'Rows: every item' : 'Rows: items with stock, orders or sales'])
    if (summary) {
      doc.kpis = [
        { label: `On hand`, value: numS(summary.onHand, st(summary, 'onHand')) }, { label: 'Physical', value: numS(summary.physicalInv, st(summary, 'physicalInv')) },
        { label: 'On order', value: numS(summary.onOrder, st(summary, 'onOrder')) }, { label: 'Total', value: numS(summary.totalQty, st(summary, 'totalQty')) },
        { label: 'Reserved', value: numS(summary.reserved, st(summary, 'reserved')) },
        { label: `PO quantity ${summary.year}`, value: numS(summary.poQtyYear, st(summary, 'poQtyYear')), secondary: `${numS(summary.poQtyPY, st(summary, 'poQtyPY'))} in ${summary.year - 1}` },
        { label: `Sold ${summary.year}`, value: numS(summary.salesQtyYear, st(summary, 'salesQtyYear')), secondary: `${numS(summary.salesQtyPY, st(summary, 'salesQtyPY'))} in ${summary.year - 1}` },
        { label: 'Inventory cost', value: cashS(summary.inventoryValue, st(summary, 'inventoryValue')) },
      ]
      doc.tables.unshift({
        title: `Item summary, ${summary.year} against ${summary.year - 1}`,
        note: 'Purchase quantities count line status 1, 2 or 3 with approval 40 or 50, for both years alike. Sales quantities are signed.',
        columns: [{ header: 'Figure' }, { header: String(summary.year), kind: 'num2' }, { header: String(summary.year - 1), kind: 'num2' }, { header: 'Difference', kind: 'num2' }],
        rows: [
          { cells: ['PO quantity', summary.poQtyYear, summary.poQtyPY, summary.poQtyDiff] },
          { cells: ['PO value', summary.poValueYear, summary.poValuePY, summary.poValueDiff] },
          { cells: ['Quantity sold', summary.salesQtyYear, summary.salesQtyPY, summary.salesQtyDiff] },
          { cells: ['Sales value', summary.salesValueYear, summary.salesValuePY, summary.salesValueDiff] },
        ],
      })
    }
    return doc
  }

  const yr = summary?.year ?? new Date().getUTCFullYear()
  const sU = dimOf(summary?.unit, summary?.unitCount)
  const sC = curOf(summary?.currencyCode, summary?.currencyCount)
  return (
    <>
      <Card>
        <CardHeader
          titleRepeatsTab={!selected}
          title={selected ? `Item ${selected}` : 'Every item in the selection'}
          subtitle={`${yr} against ${yr - 1}. The Power BI Item Status page, as it was.`}
          right={selected ? <Button onClick={() => onSelect('')}>All items</Button> : undefined}
        />
        {!summary ? <Loading label="Loading the summary…" /> : (
          /* THE ONE STATS STRIP IN THE PRODUCT THAT COULD NOT BE PUT AWAY.
              The other three Material Planning tabs, Sales and Invoice Lines
              all wrap their tiles in CollapsibleStats. This one was left out
              because the third pass was told to leave Item Status alone — and
              that meant its figures and its columns, not its furniture.
              Measured 9 Sep 2026 at 1536x864: this block stood 315px above a
              grid with 227px, ten tiles of summary over five and a half rows
              of the items being summarised.

              The card header stays OUTSIDE the collapse deliberately: it names
              the selected item and carries the All items button that clears
              the selection. A control that undoes a thing must not be hidden
              inside the thing it undoes. */
          <div className="px-4 pt-2 pb-4">
          <CollapsibleStats storageKey="stats:material-planning:item-status">
          <div className="stat-strip">
            <StatTile surface="summary" measureKey="onHand" label="On hand" value={numS(summary.onHand, st(summary, 'onHand'))} fullValue={fullQty(summary.onHand, st(summary, 'onHand'))} secondary={withDim(undefined, sU)} dense />
            <StatTile surface="summary" measureKey="physical" label="Physical" value={numS(summary.physicalInv, st(summary, 'physicalInv'))} fullValue={fullQty(summary.physicalInv, st(summary, 'physicalInv'))} secondary={withDim(undefined, sU)} dense />
            <StatTile surface="summary" measureKey="onOrder" label="On order" value={numS(summary.onOrder, st(summary, 'onOrder'))} fullValue={fullQty(summary.onOrder, st(summary, 'onOrder'))} secondary={withDim(undefined, sU)} dense />
            <StatTile surface="summary" measureKey="total" label="Total" value={numS(summary.totalQty, st(summary, 'totalQty'))} secondary={withDim('physical + on order', sU)} dense />
            <StatTile surface="summary" measureKey="reserved" label="Reserved" value={numS(summary.reserved, st(summary, 'reserved'))} fullValue={fullQty(summary.reserved, st(summary, 'reserved'))} secondary={withDim(undefined, sU)} dense />
            <StatTile surface="summary" measureKey="poQtyYear" label={`PO quantity ${yr}`} value={numS(summary.poQtyYear, st(summary, 'poQtyYear'))} secondary={withDim(`${numS(summary.poQtyPY, st(summary, 'poQtyPY'))} in ${yr - 1} · ${numS(summary.poQtyDiff, st(summary, 'poQtyDiff'))}`, sU)} dense />
            <StatTile surface="summary" measureKey="soldYear" label={`Sold ${yr}`} value={numS(summary.salesQtyYear, st(summary, 'salesQtyYear'))} secondary={withDim(`${numS(summary.salesQtyPY, st(summary, 'salesQtyPY'))} in ${yr - 1} · ${numS(summary.salesQtyDiff, st(summary, 'salesQtyDiff'))} · signed`, sU)} dense />
            <StatTile surface="summary" measureKey="poValueYear" label={`PO value ${yr}`} value={cashS(summary.poValueYear, st(summary, 'poValueYear'))} fullValue={fullCash(summary.poValueYear, st(summary, 'poValueYear'))} secondary={withCur(`${cashS(summary.poValuePY, st(summary, 'poValuePY'))} in ${yr - 1}`, sC)} dense />
            <StatTile surface="summary" measureKey="salesValueYear" label={`Sales value ${yr}`} value={cashS(summary.salesValueYear, st(summary, 'salesValueYear'))} fullValue={fullCash(summary.salesValueYear, st(summary, 'salesValueYear'))} secondary={withCur(`${cashS(summary.salesValuePY, st(summary, 'salesValuePY'))} in ${yr - 1}`, sC)} dense />
            <StatTile surface="summary" measureKey="inventoryValue" label="Inventory cost" value={cashS(summary.inventoryValue, st(summary, 'inventoryValue'))} fullValue={fullCash(summary.inventoryValue, st(summary, 'inventoryValue'))} secondary={withCur(undefined, sC)} dense />
          </div>
          </CollapsibleStats>
          </div>
        )}
      </Card>
      <ExpandableCard
        titleRepeatsTab
        breadcrumb={['Material Planning', 'Item Status']}
        title="Items"
        subtitle="One row per item. On PO counts orders whose file is open, as the Overview tile does."
        right={<ExportButtons build={buildDoc} />}
      >
        {!rows ? <Loading label="Loading items…" />
          : rows.length === 0 ? <EmptyState title="No items to show" detail="Nothing matches this filter within what you are permitted to see." />
          : <DataGrid surface="items" gridId="mp-items" columns={columns} rows={rows} {...totalsOf(rows.map((r) => r.unit))} totalRows={rows[0]?.totalRows} sort={ctx.sort} onSortChange={ctx.onSortChange} fill />}
      </ExpandableCard>
    </>
  )
}

/* ------------------------------------------------------- sales performance */

function PerformanceTab({ rows, ctx }: { rows: MpSalesPerformanceRow[] | null; ctx: Ctx }) {
  const columns = useMemo<Column<MpSalesPerformanceRow>[]>(() => [
    { key: 'item', sortKey: 'ItemNumber', header: 'Item Number', render: (r) => r.itemNumber, stickyLeft: true },
    { key: 'product', sortKey: 'ProductName', header: 'Product', render: (r) => r.productName, clamp: 36 },
    { key: 'team', sortKey: 'BusinessTeam', header: 'Business Team', render: (r) => r.businessTeam },
    { key: 'supplier', sortKey: 'Supplier', header: 'Supplier', render: (r) => r.supplier, clamp: 28 },
    { key: 'company', sortKey: 'CompanyName', header: 'Company', render: (r) => r.companyName ?? r.company },
    { key: 'unit', sortKey: 'Unit', header: 'Unit', render: (r) => r.unit },
    { key: 'qty', sortKey: 'QtySold', header: 'Quantity Sold', align: 'right', render: (r) => marked(r, 'qtySold', num(r.qtySold, dimOf(r.unit))), value: (r) => r.qtySold ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('qtySold') : undefined },
    { key: 'py', sortKey: 'QtySoldPY', header: 'Last Year', align: 'right', render: (r) => marked(r, 'qtySoldPY', num(r.qtySoldPY, dimOf(r.unit))), value: (r) => r.qtySoldPY ?? 0, total: true, withheld: ctx.v2 ? mpWithheld('qtySoldPY') : undefined },
    { key: 'diff', sortKey: 'QtyDiff', header: 'Difference', align: 'right', render: (r) => marked(r, 'qtyDiff', signed(r.qtyDiff, dimOf(r.unit))) },
    { key: 'var', sortKey: 'VariancePct', header: 'Variance %', align: 'right', // A blank variance over a visible year before it is no year to compare with, not withheld.
    render: (r) => (ctx.v2 && r.variancePct === null && r.qtySold !== null && r.qtySoldPY !== null ? NOT_RECORDED : marked(r, 'variancePct', pct(r.variancePct))) },
  ], [ctx.v2, ctx.shown])

  const buildDoc = () => !rows?.length ? null : gridDoc<MpSalesPerformanceRow>(ctx, 'mp-performance', 'Sales Performance', 'Sales performance',
    'Quantity sold per product, absolute, against the prior year.', [
    { header: 'Item Number', cell: (r) => r.itemNumber }, { header: 'Product', cell: (r) => r.productName }, { header: 'Business Team', cell: (r) => r.businessTeam },
    { header: 'Supplier', cell: (r) => r.supplier }, { header: 'Company', cell: (r) => r.companyName ?? r.company },
    { header: 'Quantity Sold', kind: 'num2', cell: (r) => r.qtySold }, { header: 'Last Year', kind: 'num2', cell: (r) => r.qtySoldPY },
    { header: 'Difference', kind: 'num2', cell: (r) => r.qtyDiff }, { header: 'Variance %', kind: 'pct', cell: (r) => r.variancePct },
    { header: 'Unit', cell: (r) => r.unit },
  ], rows, rows[0]?.totalRows, [], 'Absolute quantity, as the Power BI page counted it: a credit note adds here. The Sales Trend is signed.')

  if (!rows) return <Loading label="Loading sales…" />
  /* Asked, not inferred. This screen fetches nothing but its own rows, so it is
     the one place with no Overview to cross-check against — which is exactly why
     usp_MP_Sales_Performance carries the flag itself. Read off the first row:
     the mask is a property of the caller, so it is identical on every row. */
  const masked = rows.length > 0 && rows[0]!.canSeeQtySold === false
  return (
    <ExpandableCard
      titleRepeatsTab
      breadcrumb={['Material Planning', 'Sales']}
      title="Sales performance"
      subtitle="Quantity sold per product, absolute — a credit note adds — against the prior year."
      right={<ExportButtons build={buildDoc} />}
    >
      {masked ? <EmptyState title="Quantity sold is not shown to you" detail="This screen needs the inventory module's sales quantity." />
        : rows.length === 0 ? <EmptyState title="No sales to show" detail="No product sold in either year within this selection." />
        : <DataGrid surface="performance" gridId="mp-performance" columns={columns} rows={rows} {...totalsOf(rows.map((r) => r.unit))} totalRows={rows[0]?.totalRows} sort={ctx.sort} onSortChange={ctx.onSortChange} fill />}
    </ExpandableCard>
  )
}

/* ------------------------------------------------------------- sales trend */

function TrendTab({ rows, cards, ctx }: { rows: MpSalesMonth[] | null; cards: MpOverview | null; ctx: Ctx }) {
  /* The trend has no dimension of its own — every month is the same selection
     — so it takes it from the Overview row the tab already fetches. */
  const u = dimOf(cards?.unit, cards?.unitCount)
  const c = curOf(cards?.currencyCode, cards?.currencyCount)
  const monthName = (r: MpSalesMonth) => new Date(r.monthStart ?? '').toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' })

  const buildDoc = (): ExportDoc | null => {
    if (!rows?.length || rows.every((r) => r.qty === null)) return null
    return {
      report: 'Material Planning', page: 'Sales Trend', title: 'Sales trend',
      subtitle: `Quantity sold by month, this year against last, signed. Quantities in ${dimWord(u) ?? 'the source unit'}; value in ${dimWord(c) ?? 'the source currency'}.`,
      context: context(ctx), privileged: ctx.viewAs, currency: ctx.currency,
      tables: [{
        note: 'Every month is present whether or not it has sales. Quantities are signed: a credit note subtracts. Value is shown where permitted.',
        columns: [
          { header: 'Month' }, { header: 'Quantity', kind: 'num2' }, { header: 'Quantity Last Year', kind: 'num2' },
          { header: 'Value', kind: 'money' }, { header: 'Value Last Year', kind: 'money' },
        ],
        rows: rows.map((r) => ({ cells: [monthName(r), r.qty, r.qtyPY, r.sales, r.salesPY] })),
      }],
    }
  }

  if (!rows) return <Loading label="Loading the trend…" />
  const peak = Math.max(1, ...rows.flatMap((r) => [Math.abs(r.qty ?? 0), Math.abs(r.qtyPY ?? 0)]))
  /* Asked, not inferred — this tab fetches the Overview alongside the trend for
     its unit and currency, so the flag is already here. `!== false` rather than
     a truth test: `cards` is null for the moment before that fetch lands, and a
     missing answer is not the same as "you may not see it". */
  const masked = cards?.canSeeQtySold === false
  return (
    <Card className="flex min-h-0 flex-1 flex-col">
      <CardHeader
        titleRepeatsTab
        title="Sales trend"
        subtitle={`Quantity sold by month, this year against last, in ${dimWord(u) ?? 'the source unit'}. Twelve months, so a quiet month reads as quiet rather than as a gap.`}
        right={<ExportButtons build={buildDoc} />}
      />
      {masked ? <EmptyState title="Quantity sold is not shown to you" detail="This screen needs the inventory module's sales quantity." /> : (
        <div className="scroll-slim min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-2 p-4">
          {rows.map((r) => (
            <div key={r.monthNumber} className="flex items-center gap-3">
              <span className="w-10 shrink-0 text-(length:--text-small) text-(--color-ink-3)">{monthName(r)}</span>
              <span className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="h-3 rounded-sm bg-(--color-navy-600)" style={{ width: `${(Math.max(0, r.qty ?? 0) / peak) * 100}%` }} />
                <span className="h-3 rounded-sm bg-(--color-navy-200)" style={{ width: `${(Math.max(0, r.qtyPY ?? 0) / peak) * 100}%` }} />
              </span>
              {/* Fixed width so the bars stay comparable month to month; never
                  wrapping, because "−999.9K mixed units" fills the 112px exactly. */}
              <span className="w-28 shrink-0 whitespace-nowrap text-right text-(length:--text-small) tabular-nums text-(--color-ink)">{marked(r, 'qty', signed(r.qty, u))}</span>
              <span className="w-28 shrink-0 whitespace-nowrap text-right text-(length:--text-small) tabular-nums text-(--color-ink-3)">{marked(r, 'qtyPY', signed(r.qtyPY, u))}</span>
            </div>
          ))}
          <p className="sticky bottom-0 -mx-4 -mb-4 mt-2 bg-(--color-surface) px-4 pt-2 pb-4 text-(length:--text-small) text-(--color-ink-3)">
            <span className="mr-1 inline-block h-2 w-3 rounded-sm bg-(--color-navy-600)" /> this year
            <span className="ml-3 mr-1 inline-block h-2 w-3 rounded-sm bg-(--color-navy-200)" /> last year
            <span className="ml-3">· signed, so a month of credits reads as negative</span>
          </p>
        </div>
        </div>
      )}
    </Card>
  )
}
